import {
  customerServiceDefinition,
  customerServiceForCoreSlug,
  isCustomerServiceSlug,
  type CustomerPhotoKind,
  type CustomerServiceDefinition,
} from '../contracts/customer-services.js';
import type { CustomerServiceSlug } from '../contracts/customer-services.js';
import type {
  CustomerRequestCommentAttachmentDto,
  CustomerRequestCommentDto,
  CustomerRequestDeliverableDto,
  CustomerRequestItemDto,
  CustomerRequestResultDto,
} from '../contracts/customer-auth.js';
import type { RequestDto } from '../contracts/dto.js';
import {
  customerStageDefinition,
  customerStageFor,
  requestProgressFor,
  type CustomerLifecycleStage,
} from '../contracts/customer-lifecycle.js';
import { formatPersianDateTime, toPersianDigits } from '../contracts/persian-date.js';
import { priceSummaryFor, type PriceSummary } from '../contracts/price.js';
import { formatIranMobile, isValidIranMobile, toAsciiDigits } from '../contracts/phone.js';
import { INVALID_PHONE_MESSAGE_FA } from '../contracts/errors.js';
import { CustomerApiError } from './errors.js';
import { KeyboardChromeController, isEditableField } from './keyboard.js';
import { RequestFlow, SessionDraftStore, type RequestFlowSnapshot, type StorageLike } from './flow.js';
import { hasIcon, iconSvg } from './icons.js';
import { splitMyRequests } from './my-requests.js';
import { APPEARANCE_MODES, SERVICE_REQUEST_CSS, themeStyleFor } from './styles.js';
import { createTamoomehClient, type TamoomehClient } from './client.js';
import {
  normalizeConfig,
  resolveWidgetService,
  type CustomerClientConfig,
  type ResolvedCustomerClientConfig,
} from './config.js';
/** Fold Persian/Arabic digits and Arabic decimal separators for numeric answers. */
export function normalizeDecimalInput(value: string): string {
  return toAsciiDigits(value).replace(/\u066B/g, '.').trim();
}

/**
 * `<tamoomeh-service-request>` — the one request UI, rendered natively on both sites.
 *
 *   <!-- Tamoomeh page and Porabzar page: identical markup, no build step -->
 *   <tamoomeh-service-request service="vakil"></tamoomeh-service-request>
 *
 * A standard custom element rather than a framework component, because the two
 * consumers have nothing in common: Porabzar is 130 hand-written HTML files with
 * classic `<script>` tags and no bundler, Tamoomeh is generated static HTML.
 * Both can render a custom element; neither can render a React component.
 *
 * What that buys, concretely:
 *  - **Style isolation** — a shadow root, so the widget cannot restyle
 *    Porabzar and Porabzar's `button {}` rules cannot restyle the widget.
 *  - **No global CSS** — the stylesheet is inside the shadow root; the host page
 *    adds nothing.
 *  - **Real markup** — the element renders semantic HTML (`form`, `label`,
 *    `fieldset`, `button[type=submit]`), not a canvas or an iframe, so the
 *    request UI is keyboard- and screen-reader-navigable by construction.
 *  - **One implementation** — the service-specific differences come from the
 *    shared catalog; this file contains no per-service branch.
 *
 * The element is a thin projection of `RequestFlow`. It owns rendering, focus
 * and event delegation; it owns no request logic.
 */

export const SERVICE_REQUEST_TAG = 'tamoomeh-service-request';

/** Events the element dispatches on itself, for a host page to listen to. */
export const ELEMENT_EVENTS = {
  ready: 'tamoomeh:ready',
  requestSuccess: 'tamoomeh:request-success',
  authRequired: 'tamoomeh:auth-required',
  /**
   * The customer's authentication state changed (sign-in, sign-out, a session
   * that expired, or a restored session on load).
   *
   * Bubbles and is composed, so the host page's own chrome — the site header
   * and its account menu — can repaint the authenticated navigation the moment
   * the widget signs someone in, without a page reload. The detail carries
   * `authenticated` so the listener does not have to re-probe the session.
   */
  authChanged: 'tamoomeh:auth-changed',
  error: 'tamoomeh:error',
  /** A request needs something from the customer (today: a payment). */
  actionRequired: 'tamoomeh:action-required',
  /** The element is about to send the browser to the payment gateway. */
  paymentRedirect: 'tamoomeh:payment-redirect',
  /** The gateway is under maintenance; the card-transfer dialog was shown. */
  paymentCardTransfer: 'tamoomeh:payment-card-transfer',
} as const;

/** The dialog copy while the gateway is under maintenance. */
export const CARD_TRANSFER_MESSAGE_FA =
  'درگاه در حال تعمیر است. لطفا به صورت کارت به کارت پرداخت کنید و فیش را ارسال کنید.';

/**
 * Shortest time the Pay button stays busy before the card-transfer dialog opens.
 *
 * The server answers in milliseconds, so without a floor the spinner would
 * flash for a frame and the dialog would appear to come from nowhere.
 */
const CARD_TRANSFER_MIN_LOADING_MS = 450;

/** How long the "کپی شد" confirmation stays on the copy button. */
const COPY_FEEDBACK_MS = 2500;

/**
 * Login welcome toast — mirrors the OTP-login SMS tone (welcome + confirmed login).
 * Kept long enough to read; dismissible via the toast close control.
 */
export const LOGIN_SUCCESS_TOAST_FA = 'به تمومه خوش آمدید؛ ورود شما با موفقیت انجام شد.';

/** Request-submitted toast — mirrors the request-submitted SMS confirmation. */
export const REQUEST_SUCCESS_TOAST_FA = 'درخواست شما ثبت شد. لطفا فیش واریزی پرداخت خود رو اپلود کنید';

/** Optional supporting attachments: max count and size (must match Core). */
export const MAX_REQUEST_ATTACHMENTS = 3;
export const MAX_REQUEST_ATTACHMENT_BYTES = 5 * 1024 * 1024;

type CopyState = 'idle' | 'copying' | 'copied' | 'failed';

type AuthStep = 'choose' | 'phone' | 'otp';
type AuthBusy = null | 'request-otp' | 'verify-otp' | 'google-login' | 'submit-google-phone';
type ViewKey = 'unconfigured' | 'unknown-service' | 'form' | 'auth' | 'submitting' | 'success' | 'error';

/** Where the "درخواست‌های من" region is in its own load cycle. */
type MyRequestsPhase = 'idle' | 'loading' | 'ready' | 'error';

/**
 * A listed request, with the customer stage already resolved for its service.
 *
 * `service` is the section the request actually belongs to, resolved from the
 * row's own service identifier — never from the page the widget is embedded in.
 * The «درخواست‌های من» sheet lists every section at once, so each card must
 * render its own name, price and icon instead of the current page's.
 */
export interface MyRequestEntry {
  dto: CustomerRequestItemDto;
  service: CustomerServiceSlug;
  stage: CustomerLifecycleStage | null;
}

interface ElementState {
  view: ViewKey;
  authStep: AuthStep;
  /** Phone typed in the auth step, kept so the OTP step can resend. */
  phone: string;
  otp: string;
  /** A message for the polite live region. */
  announcement: string;
  /** Set when the element has already tried to auto-resume once. */
  autoResumed: boolean;
}

/** Shared client across every element instance on the page. */
let sharedClient: TamoomehClient | null = null;

/** Build (or reuse) the page-wide client from resolved configuration. */
export function getSharedClient(): TamoomehClient | null {
  return sharedClient;
}

export function setSharedClient(client: TamoomehClient | null): void {
  sharedClient = client;
}

/** Configure the page-wide client explicitly (used by a host bootstrap). */
export function configureClient(config: CustomerClientConfig): TamoomehClient {
  sharedClient = createTamoomehClient(config);
  return sharedClient;
}

/**
 * The one place the widget leaves the page.
 *
 * A module-level seam rather than a raw `location.assign` call, so a test can
 * observe the URL the widget would navigate to without a browser performing the
 * navigation. Production always uses the real `location`.
 */
let navigateTo: (url: string) => void = (url) => {
  if (typeof location !== 'undefined' && typeof location.assign === 'function') {
    location.assign(url);
  }
};

/** Override the navigation seam (tests); pass null to restore the browser. */
export function setNavigationHandler(handler: ((url: string) => void) | null): void {
  navigateTo =
    handler ??
    ((url) => {
      if (typeof location !== 'undefined' && typeof location.assign === 'function') {
        location.assign(url);
      }
    });
}

/**
 * Copy text to the clipboard; true when it worked.
 *
 * The async Clipboard API needs a secure context and may be refused by the
 * host page's permissions policy, so the legacy `execCommand('copy')` path is
 * kept as a fallback rather than reporting a success that did not happen.
 */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  if (typeof document === 'undefined' || !document.body) return false;
  const area = document.createElement('textarea');
  try {
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    area.style.insetBlockStart = '0';
    document.body.appendChild(area);
    area.select();
    return typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

/** Select a node's text so a customer can copy it by hand after a failed copy. */
function selectNodeText(node: HTMLElement | null): void {
  if (!node || typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  } catch {
    // Selection is a convenience; the number is still visible.
  }
}

/** Space kept between the host page's pinned chrome and whatever is revealed. */
const REVEAL_MARGIN_PX = 16;

/**
 * Where the phone and code inputs land within the visible band below the
 * pinned chrome: a little below its middle, which keeps the section heading and
 * the label in view above the input.
 */
const AUTH_INPUT_ANCHOR = 0.55;

/**
 * How much of the top of the viewport the host page's pinned chrome covers.
 *
 * Measured, never configured: the top edge is hit-tested at three points and
 * each hit is walked up to its fixed or sticky ancestor, whose bottom edge is
 * the obstruction. That is Tamoomeh's header with its service strip, a relying
 * site's own bar, or nothing — at whatever height it has right now, on this
 * viewport. An ancestor of the widget itself is not chrome, and the result is
 * capped at half the viewport so a full-screen overlay cannot push the target
 * off the page.
 */
export function topObstruction(host?: Element | null): number {
  if (typeof document === 'undefined' || typeof window === 'undefined') return 0;
  if (typeof document.elementFromPoint !== 'function') return 0;
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const height = window.innerHeight || document.documentElement.clientHeight || 0;
  let covered = 0;
  for (const x of [8, Math.round(width / 2), Math.max(0, width - 8)]) {
    for (let node = document.elementFromPoint(x, 1); node; node = node.parentElement) {
      if (node === document.body || node === document.documentElement) break;
      if (host && node.contains(host)) break;
      const position = window.getComputedStyle(node).position;
      if (position !== 'fixed' && position !== 'sticky') continue;
      const rect = node.getBoundingClientRect();
      if (rect.top <= 1) covered = Math.max(covered, rect.bottom);
      break;
    }
  }
  return Math.min(Math.max(0, covered), height / 2);
}

/**
 * Scroll the page so `target` is comfortably visible below the pinned chrome.
 *
 * `anchor` is where the target's centre lands within the visible band (0 is
 * just under the chrome, 1 the bottom of the viewport). A target taller than
 * the band is aligned to the band's top instead, so its beginning — the label —
 * is what the customer sees first. With `onlyIfObscured`, a target already
 * fully inside the band is left where it is.
 */
export function revealBelowPinnedChrome(
  target: Element,
  options: { anchor: number; onlyIfObscured?: boolean; host?: Element | null },
): void {
  if (typeof window === 'undefined' || typeof window.scrollTo !== 'function') return;
  const rect = target.getBoundingClientRect();
  // Prefer the visual viewport: on mobile the soft keyboard shrinks it and may
  // offset it (iOS), while `innerHeight` still describes the layout viewport.
  const visual = window.visualViewport;
  const viewportHeight = visual?.height || window.innerHeight || 0;
  const viewportOffsetTop = visual?.offsetTop ?? 0;
  if (viewportHeight <= 0) return;
  const bandTop = viewportOffsetTop + topObstruction(options.host) + REVEAL_MARGIN_PX;
  const bandBottom = viewportOffsetTop + viewportHeight - REVEAL_MARGIN_PX;
  const band = Math.max(0, bandBottom - bandTop);
  if (options.onlyIfObscured && rect.top >= bandTop && rect.bottom <= bandBottom) return;
  const delta =
    rect.height >= band ? rect.top - bandTop : rect.top + rect.height / 2 - (bandTop + band * options.anchor);
  if (Math.abs(delta) < 1) return;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: reduceMotion ? 'auto' : 'smooth' });
}

/**
 * Focus a control without letting the browser scroll it.
 *
 * The scroll is `revealBelowPinnedChrome`'s job: the browser's own focus
 * scroll aligns to the viewport edge, which is exactly where the host page's
 * sticky header sits. Skipped while the page itself is not focused (a
 * background tab), and for a control that cannot take focus.
 */
function focusWithoutScroll(control: HTMLElement): void {
  if (typeof document !== 'undefined' && document.hasFocus?.() === false) return;
  if ((control as HTMLInputElement).disabled) return;
  control.focus({ preventScroll: true });
}

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The stage where the customer still owes money.
 *
 * This is the same stage the card already accents
 * (`[data-stage="awaiting_payment"]`). In exactly this stage the request
 * reference is rendered as a chip, so the code itself carries the "pending
 * payment" signal; every other stage keeps the plain reference.
 */
function isPendingPaymentStage(stage: CustomerLifecycleStage | null): boolean {
  return stage === 'awaiting_payment';
}

function sessionStorageOrNull(): StorageLike | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    // Blocked by a privacy setting; fall back to memory.
    return null;
  }
}

/**
 * Base class for the custom element.
 *
 * `HTMLElement` does not exist in Node, and this module is reachable from the
 * package's main entry point, which server-side callers (Core's own tests, a
 * future SSR pass) import. Falling back to a plain class keeps the *module*
 * importable everywhere; the element still only becomes usable in a document,
 * because `defineServiceRequestElement()` refuses to register without
 * `customElements`. The alternative — a browser-only import guard — would make
 * every consumer remember which entry point is safe in which environment.
 */
const HTMLElementBase: typeof HTMLElement = (
  typeof HTMLElement === 'undefined' ? class {} : HTMLElement
) as typeof HTMLElement;

export class ServiceRequestElement extends HTMLElementBase {
  static get observedAttributes(): string[] {
    return ['service', 'appearance', 'heading', 'compact', 'bare', 'persist-draft'];
  }

  private client: TamoomehClient | null = null;
  private flow: RequestFlow | null = null;
  private definition: CustomerServiceDefinition | null = null;
  private root: ShadowRoot | null = null;
  private stage: HTMLElement | null = null;
  private liveRegion: HTMLElement | null = null;
  /**
   * Where the floating toast is mounted.
   *
   * Sits beside the re-rendered stage rather than inside it: the stage's
   * `innerHTML` is replaced on every view change, which would otherwise wipe a
   * notice the moment the customer navigated.
   */
  private toastHost: HTMLElement | null = null;
  private listeners: Array<() => void> = [];
  private state: ElementState = {
    view: 'form',
    authStep: 'choose',
    phone: '',
    otp: '',
    announcement: '',
    autoResumed: false,
  };
  private lastView: ViewKey | null = null;
  /** In-flight auth action; drives button spinners and blocks duplicate submits. */
  private authBusy: AuthBusy = null;
  private keyboardChrome: KeyboardChromeController | null = null;
  /**
   * Keep the auth view after a Google return (or pending-phone probe) even when
   * the request flow snapshot would otherwise paint the empty form.
   */
  private forceAuthView = false;
  private configProblem: string | null = null;
  /**
   * The customer's own requests, across every section they can file in.
   *
   * Kept in the element rather than in `RequestFlow`, because it is a read-only
   * projection of the server: it has no draft, no validation and nothing to
   * persist. One load is shared by two views — the in-page region, which shows
   * this section's requests, and the «درخواست‌های من» sheet, which shows them
   * all — because the server's list endpoint is already scoped to the session
   * user and returns every section in one page (each row carries its own
   * `customerService`). `myRequestsToken` guards against a slow response from a
   * previous load overwriting a newer one.
   */
  private myRequests: { phase: MyRequestsPhase; items: MyRequestEntry[]; error: string | null } = {
    phase: 'idle',
    items: [],
    error: null,
  };
  private myRequestsToken = 0;
  /** Detaches the auth subscription installed by this element's last `boot`. */
  private authUnsubscribe: (() => void) | null = null;
  /**
   * The authentication state the host page was last told about.
   *
   * Only a *transition* is announced, so a widget that re-probes the session on
   * every render cannot spam the header with identical events, and a stale
   * `authenticated: true` can never outlive a sign-out.
   */
  private lastAuthenticated = false;
  /** The request whose Pay button is currently starting a payment. */
  private payingRequest: string | null = null;
  /** Inline error from a failed payment start, for one request. */
  private paymentError: { reference: string; message: string } | null = null;
  /**
   * The open card-transfer dialog, or null.
   *
   * Rendered into its own host beside the stage, so a re-render of the request
   * list underneath cannot close it or steal its focus.
   */
  private cardTransfer: {
    reference: string;
    cardNumber: string;
    cardHolder: string | null;
    copy: CopyState;
  } | null = null;
  /** Dedicated «درخواست‌های من» bottom sheet opened from the site account menu. */
  private myRequestsSheetOpen = false;
  private modalHost: HTMLElement | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;
  /** The page's `overflow` before the dialog locked scrolling, to restore it. */
  private scrollLockPrevious: string | null = null;
  /** The request whose receipt upload is in flight. */
  private receiptBusy: string | null = null;
  /** Inline error from a failed receipt upload, for one request. */
  private receiptError: { reference: string; message: string } | null = null;
  /** One-shot success notice after a receipt is accepted, for one request. */
  private receiptNotice: { reference: string; message: string } | null = null;
  /**
   * The optional body photos the customer picked, held as `File` objects.
   *
   * A `File` cannot survive a `sessionStorage` round trip, so it is kept in
   * memory for the in-page OTP sign-in only. Reading them at submit time (as the
   * receipt form does) into this field lets an interrupted sign-in still attach
   * them; a full-page Porabzar redirect simply drops them, which is why the
   * photo fields are not rendered when the upload path is unavailable.
   */
  private pendingPhotoFiles: { kind: CustomerPhotoKind; file: File }[] = [];
  /** Per-reference upload promise, so the double submit uploads the set once. */
  private readonly photoUploads = new Map<string, Promise<void>>();
  /** One-shot feedback about the photo upload, shown on the success view. */
  private photoNotice: { tone: 'ok' | 'error'; message: string } | null = null;
  /**
   * Optional supporting attachments (up to three, 5 MiB each). Held in memory
   * like body photos so an in-page OTP round-trip keeps them.
   */
  private pendingAttachmentFiles: File[] = [];
  private readonly attachmentUploads = new Map<string, Promise<void>>();
  private attachmentNotice: { tone: 'ok' | 'error'; message: string } | null = null;
  private attachmentPickerError: string | null = null;
  /**
   * Auto-dismiss timer for the gateway-return toast.
   *
   * The notice itself is deliberately never stored on the element: `boot()`
   * shows it as a one-shot toast and strips the query params that produced it,
   * so a later render can never replay "پرداخت با موفقیت انجام شد." for a fresh
   * or unpaid request.
   */
  private returnNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback(): void {
    if (!this.root) this.buildShadow();
    this.boot();
  }

  disconnectedCallback(): void {
    this.clearReturnNoticeTimer();
    // A removed element must not leave the host page scroll-locked — both modal
    // surfaces take the lock, so both have to give it back.
    if (this.cardTransfer) this.closeCardTransfer(false);
    if (this.myRequestsSheetOpen) this.closeMyRequestsSheet();
    this.keyboardChrome?.forceClose();
    this.publishKeyboardState();
    for (const off of this.listeners) off();
    this.listeners = [];
  }

  attributeChangedCallback(name: string, previous: string | null, next: string | null): void {
    if (previous === next) return;
    if (!this.isConnected || !this.root) return;
    if (name === 'service') {
      const slug = this.resolvedServiceSlug();
      if (slug && this.flow && slug !== this.flow.currentService) {
        this.flow.setService(slug);
        this.definition = customerServiceDefinition(slug);
        this.pendingPhotoFiles = [];
        this.photoNotice = null;
        this.pendingAttachmentFiles = [];
        this.attachmentNotice = null;
        this.attachmentPickerError = null;
        this.forceAuthView = false;
        this.state.view = 'form';
        this.hideReturnNotice();
        this.myRequestsToken += 1;
        this.myRequests = { phase: 'idle', items: [], error: null };
        this.applyTheme();
        this.render(true);
        void this.loadMyRequests();
      } else if (!slug) {
        this.boot();
      }
      return;
    }
    this.applyTheme();
    this.render(true);
  }

  // ---- public API ---------------------------------------------------------

  /** The page-wide client this element is using. */
  get tamoomehClient(): TamoomehClient | null {
    return this.client;
  }

  /** The flow controller, for a host that wants to drive or observe it. */
  get requestFlow(): RequestFlow | null {
    return this.flow;
  }

  /** The catalog entry currently rendered. */
  get serviceDefinition(): CustomerServiceDefinition | null {
    return this.definition;
  }

  /** Discard the draft and return to the empty form. */
  reset(): void {
    // A new request is not the request the gateway just settled, so a success
    // toast from that return must not linger over an empty, unpaid form.
    this.hideReturnNotice();
    this.flow?.reset();
    this.pendingPhotoFiles = [];
    this.photoNotice = null;
    this.pendingAttachmentFiles = [];
    this.attachmentNotice = null;
    this.attachmentPickerError = null;
    this.state.otp = '';
    this.state.authStep = 'choose';
    this.authBusy = null;
    this.forceAuthView = false;
    this.state.view = 'form';
    this.render(true);
    void this.loadMyRequests();
  }

  /** Re-probe the customer session. */
  async refreshSession(): Promise<void> {
    if (!this.client) return;
    await this.client.auth.refresh();
    this.render(true);
    await this.loadMyRequests();
  }

  /**
   * Reload the signed-in customer's requests for this section.
   *
   * Exposed so a host page can refresh the list after its own payment flow.
   */
  async refreshRequests(): Promise<void> {
    await this.loadMyRequests();
  }

  /** The customer's requests currently rendered, newest first. */
  get myRequestItems(): readonly MyRequestEntry[] {
    return this.myRequests.items;
  }

  // ---- my requests --------------------------------------------------------

  /**
   * Load the signed-in customer's requests — every section in one page.
   *
   * The server scopes the query to the session user, so the same request serves
   * both views: the in-page region filters the result down to this section, and
   * the sheet shows all of it. The client additionally drops anything that is not
   * this customer's or that names an unknown section, so a server regression
   * cannot paint another customer's request into this widget.
   */
  private async loadMyRequests(options?: { silent?: boolean }): Promise<void> {
    if (!this.client || !this.definition) return;
    const auth = this.client.auth;

    if (!auth.isAuthenticated) {
      this.myRequests = { phase: 'idle', items: [], error: null };
      this.renderMyRequests();
      return;
    }

    const token = ++this.myRequestsToken;
    // A silent refresh keeps the current list on screen while re-reading it:
    // replacing the region with a loading skeleton mid-upload collapses the
    // open receipt form, hides its spinner and jumps the page scroll.
    const silent = options?.silent === true && this.myRequests.phase === 'ready';
    if (!silent) {
      this.myRequests = { phase: 'loading', items: [], error: null };
      this.renderMyRequests();
    }

    try {
      // No `service` filter: the endpoint already returns every section the
      // customer has filed in, which is what the all-services sheet needs.
      const rows = await this.client.requests.list({ pageSize: 20 });
      if (token !== this.myRequestsToken) return;
      const customerId = auth.snapshot.currentUser?.id ?? null;
      const items: MyRequestEntry[] = [];
      for (const row of rows) {
        if (!this.belongsToThisCustomer(row, customerId)) continue;
        const service = this.serviceSlugOf(row);
        if (!service) continue;
        items.push({ dto: row, service, stage: customerStageFor(service, row.status) });
      }
      this.myRequests = { phase: 'ready', items, error: null };
    } catch (error) {
      if (token !== this.myRequestsToken) return;
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      // A silent refresh must never wipe the visible list into an error
      // skeleton: the receipt flow reports its own inline error instead.
      if (silent) return;
      this.myRequests = { phase: 'error', items: [], error: normalized.messageFa };
      // An expired session is an authentication change, not a list failure: the
      // probe flips the client back to signed out, which repaints the host
      // header instead of leaving a stale signed-in chrome on screen. Guarded on
      // the current state, so a probe that has already flipped cannot re-enter.
      if (normalized.requiresAuth && auth.isAuthenticated) void auth.refresh();
    }
    this.renderMyRequests();
  }

  /**
   * Announce every real authentication transition to the host page.
   *
   * This is what makes the site header show «لیست درخواست‌های من» and «خروج از
   * حساب» the moment a customer signs in through the widget. `CustomerAuth`
   * notifies its subscribers on every state patch; the element turns a genuine
   * *transition* into one bubbling, composed `auth-changed` event, and the host
   * page's own chrome repaints from that event. There is no page reload, no
   * polling and no timer — the header is repainted by the same state change that
   * authenticated the widget.
   *
   * Subscribing happens once per `boot`, and the previous subscription is always
   * detached first, so a widget that re-boots (a section change, a re-attach)
   * cannot accumulate listeners and repaint the header twice.
   */
  private watchAuth(): void {
    const client = this.client;
    this.authUnsubscribe?.();
    this.authUnsubscribe = null;
    if (!client) return;
    // Seeded from the current state so only a *change* is announced: the initial
    // notification every subscriber receives is not a transition.
    this.lastAuthenticated = client.auth.isAuthenticated;
    const off = client.auth.subscribe((state) => {
      // `ready` is false until the first session probe settles; a state before
      // that is not a customer transition and must not repaint host chrome.
      if (!state.ready) return;
      if (state.isAuthenticated === this.lastAuthenticated) return;
      this.lastAuthenticated = state.isAuthenticated;
      this.announceAuthChange(state.isAuthenticated);
    });
    this.authUnsubscribe = off;
    this.listeners.push(() => {
      off();
      if (this.authUnsubscribe === off) this.authUnsubscribe = null;
    });
  }

  private announceAuthChange(authenticated: boolean): void {
    this.dispatchEvent(
      new CustomEvent(ELEMENT_EVENTS.authChanged, {
        bubbles: true,
        composed: true,
        detail: { authenticated, service: this.definition?.slug ?? null },
      }),
    );
    // The list is scoped to whoever is signed in, so a transition reloads it.
    void this.loadMyRequests();
  }

  /** Defence in depth: a request that is not this customer's is never shown. */
  private belongsToThisCustomer(row: RequestDto, customerId: string | null): boolean {
    if (!customerId) return false;
    return row.userId === customerId;
  }

  /** The customer section a listed request belongs to, or null when unknown. */
  private serviceSlugOf(row: RequestDto & { customerService?: string | null }): CustomerServiceSlug | null {
    if (isCustomerServiceSlug(row.customerService)) {
      return row.customerService;
    }
    return customerServiceForCoreSlug(row.serviceSlug)?.slug ?? null;
  }

  /** Replace only the list region, so typing in the form is never disturbed. */
  private renderMyRequests(): void {
    if (!this.root) return;
    const region = this.root.querySelector<HTMLElement>('[data-region="my-requests"]');
    if (region) region.innerHTML = this.myRequestsHtml({ scope: 'section' });
    if (this.myRequestsSheetOpen) this.paintMyRequestsSheet();
  }

  /**
   * The requests markup, for one of its two homes.
   *
   * Both scopes render the same cross-service dataset through the shared
   * `splitMyRequests` helper: incomplete requests first (no section heading),
   * then the collapsible «درخواست‌های تکمیل‌شده» section. The current service
   * only affects ordering within each section — never membership. `scope:
   * 'section'` is the in-page region under the request form (with its own
   * heading); `scope: 'all'` is the body of the «درخواست‌های من» sheet, whose
   * dialog header already carries the title, so `heading: false` keeps exactly
   * one «درخواست‌های من» on screen.
   */
  private myRequestsHtml(options: { scope: 'section' | 'all'; heading?: boolean }): string {
    const definition = this.definition;
    if (!definition) return '';
    const nameFa = esc(definition.nameFa);
    const showHeading = options.heading !== false;
    const head = showHeading
      ? `
      <div class="mine-head">
        <h3 id="mine-${definition.slug}-title" part="my-requests-heading">درخواست‌های من</h3>
        <p>درخواست‌هایی که با این شماره موبایل در بخش ${nameFa} ثبت کرده‌اید.</p>
      </div>`
      : '';

    const auth = this.client?.auth;
    if (!auth?.isAuthenticated) {
      const note = showHeading
        ? `برای دیدن درخواست‌های قبلی خود در بخش ${nameFa}، با شماره موبایل وارد شوید.`
        : 'برای دیدن درخواست‌های قبلی خود، با شماره موبایل وارد شوید.';
      return showHeading
        ? `
      <div class="mine-head">
        <h3 id="mine-${definition.slug}-title" part="my-requests-heading">درخواست‌های من</h3>
        <p>${note}</p>
      </div>`
        : `<p class="mine-note" role="status">${note}</p>`;
    }

    const { phase, error } = this.myRequests;
    // Both homes share one dataset, one completion split and one ordering:
    // the current service sorts first but never filters anything out.
    const { incomplete, completed } = splitMyRequests(this.myRequests.items, definition.slug);
    if (phase === 'idle' || phase === 'loading') {
      return `${head}<p class="mine-note" role="status">در حال بارگذاری درخواست‌ها…</p>`;
    }
    if (phase === 'error') {
      return `${head}
        <p class="mine-note" role="alert">${esc(error ?? 'بارگذاری درخواست‌ها انجام نشد.')}</p>
        <div class="actions">
          <button class="btn secondary" type="button" data-action="refresh-requests">${iconSvg('refresh', 18)} تلاش دوباره</button>
        </div>`;
    }
    if (incomplete.length === 0 && completed.length === 0) {
      const empty = showHeading
        ? `
          <p><strong>هنوز درخواستی در این بخش ثبت نکرده‌اید.</strong></p>
          <p>وقتی درخواست‌تان را ثبت کنید، وضعیت و مراحل آن همین‌جا نمایش داده می‌شود.</p>`
        : `
          <p><strong>هنوز درخواستی ثبت نکرده‌اید.</strong></p>
          <p>وقتی درخواست‌تان را ثبت کنید، وضعیت و مراحل آن همین‌جا نمایش داده می‌شود.</p>`;
      return `${head}
        <div class="mine-empty" part="my-requests-empty">
          <span class="mine-empty-icon" aria-hidden="true">${iconSvg('inbox', 26)}</span>
          ${empty}
        </div>`;
    }
    const open = incomplete.length > 0
      ? `<ul class="mine-list mine-open">${incomplete.map((item) => this.requestCardHtml(item)).join('')}</ul>`
      : '';
    return `${head}${open}${this.completedRequestsHtml(completed)}`;
  }

  /**
   * The completed requests section: one collapsed disclosure.
   *
   * A native `<details>` (closed by default) keeps the toggle keyboard- and
   * screen-reader-operable with no script: `summary` is focusable, Enter/Space
   * toggles it, and the open state is exposed to assistive technology.
   * Nothing renders when there is nothing completed.
   */
  private completedRequestsHtml(completed: MyRequestEntry[]): string {
    if (completed.length === 0) return '';
    const count = completed.length;
    return `
      <details class="mine-completed" data-completed-section>
        <summary part="my-requests-completed-toggle">
          <span class="mine-completed-title">درخواست‌های تکمیل‌شده</span>
          <span class="mine-completed-count" aria-label="${toPersianDigits(count)} درخواست تکمیل‌شده">${toPersianDigits(count)}</span>
          <span class="req-caret" aria-hidden="true">${iconSvg('chevronDown', 18)}</span>
        </summary>
        <ul class="mine-list">${completed.map((item) => this.requestCardHtml(item)).join('')}</ul>
      </details>`;
  }

  /**
   * The compact service chip that names the section a request belongs to.
   *
   * The label and the icon both come from the request's own resolved section
   * identifier — the same catalog entry the request was filed against — so the
   * chip can never be inferred from a request's title and cannot disagree with
   * the service that fulfils it. Rendered on every card, which is what makes a
   * mixed-service list readable at a glance.
   */
  private serviceChipHtml(service: CustomerServiceSlug): string {
    const definition = customerServiceDefinition(service);
    if (!definition) return '';
    return `<span class="req-svc" part="request-service" data-service="${esc(definition.slug)}">
              <span class="req-svc-icon" aria-hidden="true">${iconSvg(definition.icon, 14)}</span>
              <span class="req-svc-label">${esc(definition.nameFa)}</span>
            </span>`;
  }

  /**
   * The price the customer is about to pay, shown wherever they confirm or
   * initiate payment.
   *
   * The figures come from the section's catalog entry via the shared
   * `contracts/price.ts`, so this «۱٬۰۰۰٬۰۰۰ تومان» is the same amount Core
   * writes onto the request row and charges — never a client-typed number. The
   * struck-through list price and the derived discount are presentation only.
   *
   * Renders nothing (rather than a fabricated or partial figure) when the
   * section has no usable amount, so a missing price is silent and safe.
   */
  private checkoutPriceHtml(
    kind: 'card' | 'success' | 'transfer',
    definition: CustomerServiceDefinition | null = this.definition,
  ): string {
    const summary = priceSummaryFor(definition);
    if (!summary) return '';
    const list =
      summary.listFa && summary.discountFa
        ? `<span class="price-list">${esc(summary.listFa)}</span>`
        : '';
    const discount = summary.discountFa
      ? `<span class="price-off">${esc(summary.discountFa)}</span>`
      : '';
    const duration =
      typeof definition?.durationFa === 'string' && definition.durationFa.trim()
        ? `<span class="price-duration">${esc(definition.durationFa.trim())}</span>`
        : '';
    return `<p class="checkout-price" data-checkout-price="${esc(kind)}" data-amount="${esc(
      String(definition?.defaultAmount ?? ''),
    )}">
      ${list}
      <span class="price-sale">${esc(summary.saleFa)}</span>
      ${duration}
      ${discount}
    </p>`;
  }

  /**
   * One request as a timeline card: summary + expandable detail.
   *
   * The card renders from the *request's own* section, not from the section the
   * widget happens to be embedded in: the all-services sheet shows legal, sports
   * and child requests side by side, and each one must keep its own name, price
   * and duration.
   */
  private requestCardHtml(entry: MyRequestEntry): string {
    const definition = customerServiceDefinition(entry.service);
    if (!definition) return '';
    const request = entry.dto;
    const stage = entry.stage;
    const stageDefinition = customerStageDefinition(stage);
    const reference = request.publicId;
    const date = formatPersianDateTime(request.createdAt);
    const stageTitle = stageDefinition?.titleFa ?? 'در حال به‌روزرسانی وضعیت';
    const explanation =
      stageDefinition?.explanationFa ??
      'وضعیت این درخواست در حال به‌روزرسانی است. برای پیگیری با پشتیبانی تمومه تماس بگیرید.';

    const track = this.requestTrackHtml(entry);
    const pendingPayment = isPendingPaymentStage(stage);
    const canPay = request.canPay === true && (this.client?.payments.available ?? false);
    const paying = this.payingRequest === reference;
    const payError = this.paymentError?.reference === reference ? this.paymentError.message : null;

    // The Pay button is the customer's one action while the request is awaiting
    // payment. It is shown only when the server says `canPay`. The receipt
    // option sits beside it as the gateway-unavailable fallback, and is likewise
    // shown only when the server says `canSubmitReceipt`.
    // The price sits above both paths whenever money is still owed — online
    // (canPay) or by receipt (canSubmitReceipt). Both settle the *same*
    // authoritative amount, so the figure must appear even when only the
    // receipt path is offered.
    const owesMoney = canPay || request.canSubmitReceipt === true;
    const price = owesMoney ? this.checkoutPriceHtml('card', definition) : '';

    const payBlock = canPay
      ? `<div class="actions">
          <button class="btn pay" type="button" data-action="pay" data-request="${esc(reference)}"${
            paying ? ' disabled aria-busy="true"' : ''
          }>
            ${iconSvg('card', 18)} پرداخت ${esc(priceSummaryFor(definition)?.saleFa ?? '')}
          </button>
          ${paying ? `<span class="spinner" aria-hidden="true"></span>` : ''}
        </div>
        ${payError ? `<p class="error" role="alert">${esc(payError)}</p>` : ''}
        <p class="req-hint">${esc(stageDefinition?.actionHintFa ?? '')}</p>`
      : stageDefinition?.actionRequired
        ? `<div class="actions">
            <button class="btn secondary" type="button" data-action="request-help" data-request="${esc(reference)}">
              راهنمای پرداخت
            </button>
          </div>
          <p class="req-hint">${esc(stageDefinition.actionHintFa ?? '')}</p>`
        : '<p class="req-hint">در این مرحله نیازی به اقدام از طرف شما نیست.</p>';
    const action = `${price}${payBlock}${this.receiptAreaHtml(
      reference,
      request.canSubmitReceipt,
      request.receiptBlockedReason,
    )}`;
    const messages = this.expertMessagesHtml(request.comments ?? [], request.deliverables ?? []);

    return `
      <li class="req" data-stage="${esc(stage ?? 'unknown')}" data-request="${esc(reference)}" data-service="${esc(definition.slug)}">
        <details>
          <summary part="request-summary">
            <span class="req-ref code${pendingPayment ? ' req-chip' : ''}" dir="ltr">${esc(reference)}</span>
            <span class="req-stage">${esc(stageTitle)}</span>
            <span class="req-date"><time datetime="${esc(request.createdAt)}">${esc(date)}</time></span>
            ${this.serviceChipHtml(entry.service)}
            <span class="req-caret" aria-hidden="true">${iconSvg('chevronDown', 18)}</span>
          </summary>
          <div class="req-body">
            <p class="req-explain">${esc(explanation)}</p>
            ${track}
            ${action}
            ${messages}
            <details class="req-secondary">
              <summary>جزئیات درخواست</summary>
              <dl class="req-meta">
                <div><dt>شماره پیگیری</dt><dd class="code" dir="ltr">${esc(reference)}</dd></div>
                <div><dt>خدمت</dt><dd>${esc(definition.nameFa)}</dd></div>
                <div><dt>تاریخ ثبت</dt><dd><time datetime="${esc(request.createdAt)}">${esc(date)}</time></dd></div>
                <div><dt>وضعیت</dt><dd>${esc(stageTitle)}</dd></div>
              </dl>
            </details>
          </div>
        </details>
      </li>`;
  }

  /**
   * The expert's messages and deliverables on one request.
   *
   * This is the customer's window into what happened after payment: the notes an
   * operator wrote in the admin panel, the files attached to those messages,
   * and the programme they were handed. It is rendered inside the request
   * card's detail so it is present whenever the customer opens the request,
   * and omitted entirely when there is nothing to show rather than printing
   * an empty heading.
   *
   * Every attachment URL is server-built and request-guarded
   * (the customer-scoped file route); the widget never constructs one and
   * never exposes a storage path.
   */
  private expertMessagesHtml(
    comments: readonly CustomerRequestCommentDto[],
    deliverables: readonly CustomerRequestDeliverableDto[],
  ): string {
    if (comments.length === 0 && deliverables.length === 0) return '';
    const commentsBlock =
      comments.length > 0
        ? `<section class="req-messages" aria-label="پیام‌های کارشناس">
            <h4 class="req-sub">پیام‌های کارشناس</h4>
            <ul>
              ${comments
                .map(
                  (comment) => `<li>
                    <p>${esc(comment.body)}</p>
                    ${this.commentAttachmentsHtml(comment.attachments ?? [])}
                    <time datetime="${esc(comment.createdAt)}">${esc(formatPersianDateTime(comment.createdAt))}</time>
                  </li>`,
                )
                .join('')}
            </ul>
          </section>`
        : '';
    const deliverablesBlock =
      deliverables.length > 0
        ? `<section class="req-deliverables" aria-label="برنامه و فایل‌های ارسالی">
            <h4 class="req-sub">برنامه و فایل‌های ارسالی</h4>
            <ul>
              ${deliverables
                .map((deliverable) => `<li>${this.deliverableFileHtml(deliverable)}</li>`)
                .join('')}
            </ul>
          </section>`
        : '';
    return `${commentsBlock}${deliverablesBlock}`;
  }

  /**
   * Resolve a server-built customer file path against the API origin.
   *
   * The DTO carries a path (`/customer/files/…`) served by Core, but this
   * widget runs on the static site host — a relative link would resolve
   * against the page origin and land on static hosting (a 404) instead of
   * the customer-scoped file route. Prefixing the configured API base keeps
   * preview and download on the host that owns the session cookie and the
   * ownership/visibility checks. Absolute URLs pass through untouched, and
   * with no API base configured (SSO bridge mode) the path is left relative
   * so it keeps resolving against the same-origin bridge.
   */
  private customerFileHref(fileUrl: string): string {
    if (!fileUrl || /^https?:\/\//i.test(fileUrl)) return fileUrl;
    const base = (this.client?.config.apiBaseUrl ?? '').replace(/\/+$/, '');
    if (!base || !fileUrl.startsWith('/')) return fileUrl;
    return `${base}${fileUrl}`;
  }

  /**
   * One coach-uploaded programme file: a preview plus an explicit download.
   *
   * The preview decision stays server-side (`?preview=1` renders inline only
   * for images/PDF and forces every other type to download); the download
   * link carries a `download` attribute. A record without a server-built URL
   * is stated, never linked.
   */
  private deliverableFileHtml(deliverable: CustomerRequestDeliverableDto): string {
    const program = deliverable.programText
      ? `<p class="req-program">${esc(deliverable.programText)}</p>`
      : '';
    const rawUrl = typeof deliverable.fileUrl === 'string' ? deliverable.fileUrl : '';
    const fileUrl = this.customerFileHref(rawUrl);
    const label =
      typeof deliverable.fileName === 'string' && deliverable.fileName.trim()
        ? deliverable.fileName.trim()
        : 'فایل برنامه';
    const file =
      fileUrl
        ? `<span class="req-attachment-name">${esc(label)}</span>
           <a class="link" href="${esc(`${fileUrl}${fileUrl.includes('?') ? '&' : '?'}preview=1`)}" target="_blank" rel="noopener">پیش‌نمایش</a>
           <a class="link" href="${esc(fileUrl)}" download rel="noopener">دانلود</a>`
        : '';
    return `${program}${file}
      <time datetime="${esc(deliverable.createdAt)}">${esc(formatPersianDateTime(deliverable.createdAt))}</time>`;
  }

  /**
   * One message's file attachments: a preview plus an explicit download.
   *
   * Images and PDFs preview inline through the existing authenticated file
   * route (`?preview=1` renders inline only for those viewable types; the
   * server forces every other type to download). Anything else — or a file
   * whose type is unknown — renders a download-only fallback with the same
   * filename, never a broken embed. Each entry names the file (or a fallback
   * label) and exposes both actions as real links, so they work with a
   * keyboard, a screen reader and a touch screen alike. Nothing renders when
   * the message carries no files.
   */
  private commentAttachmentsHtml(
    attachments: readonly CustomerRequestCommentAttachmentDto[],
  ): string {
    if (attachments.length === 0) return '';
    const items = attachments
      .map((attachment) => {
        const label = attachment.fileName?.trim() ? attachment.fileName.trim() : 'پیوست پیام کارشناس';
        const rawUrl = typeof attachment.fileUrl === 'string' ? attachment.fileUrl : '';
        const fileUrl = this.customerFileHref(rawUrl);
        if (!fileUrl) {
          // A record without a server-built URL is stated, never linked: a
          // missing file must not become a broken control.
          return `<li class="req-attachment" data-attachment="${esc(attachment.publicId)}">
            <span class="req-attachment-name">${esc(label)}</span>
            <span class="req-hint" role="note">فایل در حال حاضر در دسترس نیست.</span>
          </li>`;
        }
        const preview = this.attachmentPreviewHtml(attachment, label, fileUrl);
        return `<li class="req-attachment" data-attachment="${esc(attachment.publicId)}">
          <span class="req-attachment-name">${esc(label)}</span>
          ${preview}
          <a class="link" href="${esc(fileUrl)}" download rel="noopener">دانلود</a>
        </li>`;
      })
      .join('');
    return `<ul class="req-attachments" aria-label="پیوست‌های پیام">${items}</ul>`;
  }

  /** Inline preview for viewable types; empty string for a download-only file. */
  private attachmentPreviewHtml(
    attachment: CustomerRequestCommentAttachmentDto,
    label: string,
    fileUrl: string,
  ): string {
    const mime = (attachment.mimeType ?? '').toLowerCase();
    const previewUrl = `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}preview=1`;
    if (mime.startsWith('image/')) {
      return `<a class="req-attachment-preview" href="${esc(previewUrl)}" target="_blank" rel="noopener" aria-label="پیش‌نمایش ${esc(label)}">
        <img src="${esc(previewUrl)}" alt="${esc(label)}" loading="lazy" />
      </a>`;
    }
    if (mime === 'application/pdf') {
      return `<embed class="req-attachment-preview req-attachment-pdf" src="${esc(previewUrl)}" type="application/pdf" aria-label="پیش‌نمایش ${esc(label)}" />`;
    }
    return `<span class="req-hint">پیش‌نمایش برای این نوع فایل پشتیبانی نمی‌شود؛ از دانلود استفاده کنید.</span>`;
  }

  /**
   * The five-step request progress, mapped from persisted backend state.
   *
   * Order is fixed: ایجاد درخواست، در انتظار پرداخت، در انتظار تأیید پرداخت،
   * تأیید پرداخت، منتظر تماس باشید. The mapping itself lives in the shared
   * contracts layer (`requestProgressFor`) so the widget and any test render
   * the same steps from the same persisted facts; this method only turns those
   * steps into markup. Cancelled requests show no track (the outcome is named
   * above the card); completed requests show every step done.
   */
  private requestTrackHtml(entry: MyRequestEntry): string {
    const request = entry.dto as unknown as Record<string, unknown>;
    const status = typeof request['status'] === 'string' ? (request['status'] as string) : null;
    if (String(status ?? '').toUpperCase() === 'CANCELLED') return '';
    const payment = (request['payment'] ?? null) as { status?: unknown } | null;
    const paymentStatus = payment && typeof payment.status === 'string' ? payment.status : null;
    const receiptBlockedReason =
      typeof request['receiptBlockedReason'] === 'string'
        ? (request['receiptBlockedReason'] as string)
        : undefined;
    // The canonical persisted coach-call flag lives on the request metadata
    // (promoted out of the sport form's answers at creation). Only the sports
    // consultation (`morabi`) may relabel the final step; every other service
    // keeps «منتظر تماس باشید» regardless of this value.
    const metadata =
      request['metadata'] !== null && typeof request['metadata'] === 'object'
        ? (request['metadata'] as Record<string, unknown>)
        : null;
    const wantsCall = metadata?.['wantsCall'] === true ? true : metadata?.['wantsCall'] === false ? false : undefined;
    const steps = requestProgressFor({
      requestStatus: status,
      paymentStatus,
      receiptBlockedReason,
      customerService: entry.service,
      wantsCall,
    });
    const items = steps
      .map((step) => {
        const currentAttribute = step.state === 'current' ? ' aria-current="step"' : '';
        const check = step.state === 'done' ? '<span class="req-check" aria-hidden="true">✓</span>' : '';
        return `<li data-state="${step.state}"${currentAttribute}>
          <span class="req-dot" aria-hidden="true">${check}</span>
          <span class="req-step-title">${esc(step.titleFa)}</span>
        </li>`;
      })
      .join('');
    return `<ol class="req-track" aria-label="مراحل درخواست">${items}</ol>`;
  }

  /**
   * The manual payment receipt area for one request.
   *
   * Shown only when the server says the receipt is acceptable *and* this
   * deployment can actually deliver it (`payments.receiptAvailable`), so a
   * relying site without a bridge endpoint never renders a form that cannot
   * submit. A submission already waiting for review is stated plainly instead of
   * offering a button the server would refuse.
   */
  private receiptAreaHtml(
    reference: string,
    canSubmitReceipt: boolean | undefined,
    receiptBlockedReason: string | undefined,
  ): string {
    const notice = this.receiptNotice?.reference === reference ? this.receiptNotice.message : null;
    if (notice) return `<p class="ok" role="status">${esc(notice)}</p>`;

    const available = canSubmitReceipt === true && (this.client?.payments.receiptAvailable ?? false);
    if (!available) {
      return receiptBlockedReason === 'PENDING_REVIEW'
        ? '<p class="req-hint">رسید پرداخت شما ثبت شده و در انتظار بررسی کارشناس است.</p>'
        : '';
    }

    const busy = this.receiptBusy === reference;
    const error = this.receiptError?.reference === reference ? this.receiptError.message : null;
    const id = `receipt-${reference}`;
    // While an upload is in flight (or its error is shown) the disclosure
    // stays open: a re-render that collapses it would hide the spinner,
    // collapse the page height and jump the scroll position.
    const openAttribute = busy || error ? ' open' : '';
    return `
      <details class="receipt-upload" data-receipt${openAttribute}>
        <summary class="receipt-summary">${iconSvg('card', 16)} ارسال رسید پرداخت</summary>
        <p class="receipt-hint">اگر درگاه پرداخت در دسترس نیست، تصویر یا فایل PDF رسید پرداخت خود را ارسال کنید تا کارشناس آن را بررسی کند.</p>
        <form class="receipt-form" data-action="submit-receipt" data-request="${esc(reference)}" novalidate>
          <div class="field">
            <label for="${esc(id)}-file">فایل رسید پرداخت</label>
            <input id="${esc(id)}-file" name="receipt" type="file" accept="image/*,application/pdf" required />
            <p class="hint">تصویر یا PDF، حداکثر ۵ مگابایت.</p>
          </div>
          <div class="field">
            <label for="${esc(id)}-desc">توضیح دهنده رسید</label>
            <textarea id="${esc(id)}-desc" name="description" rows="3"
              placeholder="مثلاً: کارت به کارت از بانک ملت، شماره پیگیری ۱۲۳۴۵"></textarea>
            <p class="hint">توضیح اختیاری؛ هر توضیحی که به بررسی کارشناس کمک کند.</p>
          </div>
          <div class="actions">
            <button class="btn" type="submit"${busy ? ' disabled aria-busy="true"' : ''} data-receipt-submit>
              ${busy ? '<span class="spinner" aria-hidden="true"></span> در حال ارسال…' : `${iconSvg('card', 18)} ارسال رسید پرداخت`}
            </button>
          </div>
          ${error ? `<p class="error" role="alert">${esc(error)}</p>` : ''}
        </form>
      </details>`;
  }

  /**
   * Patch one receipt form's submit button into/out of its loading state.
   *
   * In place, never a re-render: the form holds the picked `File`, the open
   * disclosure and the customer's scroll position, and all three would be lost
   * by replacing the list region mid-upload.
   */
  private setReceiptSubmitBusy(form: HTMLElement, busy: boolean): void {
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"][data-receipt-submit]');
    if (!button) return;
    if (busy) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.innerHTML = '<span class="spinner" aria-hidden="true"></span> در حال ارسال…';
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Read the receipt form's file and description from the DOM and upload them.
   *
   * The file is never stored on the element: a `File` in render state would be
   * dropped by the next `innerHTML` pass and could not survive a re-render
   * anyway. Reading it at submit time is both simpler and correct.
   */
  private async submitReceipt(form: HTMLElement): Promise<void> {
    if (!this.client) return;
    const reference = (form.getAttribute('data-request') ?? '').trim();
    if (!reference) return;

    const fileInput = form.querySelector<HTMLInputElement>('input[type="file"][name="receipt"]');
    const descriptionInput = form.querySelector<HTMLTextAreaElement>('textarea[name="description"]');
    const file = fileInput?.files?.[0] ?? null;
    const description = (descriptionInput?.value ?? '').trim();

    // The server accepts an empty description as optional; a client-side guard
    // is retained for the one required control (the file).
    const problems: { control: HTMLElement | null; message: string | null }[] = [
      { control: fileInput, message: file ? null : 'فایل رسید پرداخت را انتخاب کنید.' },
    ];
    // Marked in place rather than re-rendered: a re-render would drop the file
    // the customer already picked.
    for (const { control, message } of problems) {
      if (control) this.markReceiptField(form, control, message);
    }
    if (!file || problems.some((problem) => problem.message)) {
      this.revealFirstInvalidField(form);
      return;
    }

    // The button stays disabled and the form is never submitted twice for the
    // same request while an upload is in flight.
    if (this.receiptBusy === reference) return;
    this.receiptBusy = reference;
    this.receiptError = null;
    this.receiptNotice = null;
    // Patch the pressed form in place: a full re-render here would replace the
    // open <details> with a collapsed one, hiding the spinner, dropping the
    // picked file's focus and jumping the page scroll mid-upload.
    this.setReceiptSubmitBusy(form, true);

    try {
      await this.client.payments.submitReceipt(reference, { file, description });
      // Clear local form state only after the backend confirms success. The
      // subsequent `loadMyRequests()` re-render closes the receipt section for
      // this request (it becomes pending review), so the picked file is gone.
      if (fileInput) fileInput.value = '';
      if (descriptionInput) descriptionInput.value = '';
      this.receiptError = null;
      this.receiptNotice = {
        reference,
        message: 'رسید پرداخت شما با موفقیت ثبت شد و برای بررسی کارشناس ارسال می‌شود.',
      };
      this.announce(this.receiptNotice.message);
      this.showReturnNotice({ tone: 'success', message: this.receiptNotice.message });
      // The server now reports the receipt as pending review, so the list is
      // re-read rather than patched: the form disappears and the hint replaces it.
      // Controls stay disabled until the refresh completes to prevent duplicates.
      // Silent: keep the busy form on screen during the re-read instead of
      // flashing a loading skeleton that jumps the scroll.
      await this.loadMyRequests({ silent: true });
      this.receiptBusy = null;
      this.renderMyRequests();
      this.scrollToMyRequests(reference);
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      this.receiptBusy = null;
      this.receiptError = { reference, message: normalized.messageFa };
      this.announce(normalized.messageFa);
      this.renderMyRequests();
    }
  }

  /**
   * Bring the «درخواست‌های من» section into view after a successful receipt
   * submit.
   *
   * The section region is the target (not the individual card): the receipt
   * form unmounts on success and the card moves between groups, so aiming at
   * the section keeps the scroll stable. `revealBelowPinnedChrome` lands it
   * below the host page's sticky header; the `scrollIntoView` call is kept as
   * a best-effort fallback for hosts where measuring the chrome fails.
   */
  private scrollToMyRequests(requestPublicId?: string | null): void {
    const host = this.root;
    if (!host) return;
    let requestCard: HTMLElement | null = null;
    if (requestPublicId) {
      for (const node of host.querySelectorAll<HTMLElement>('.req[data-request]')) {
        if (node.getAttribute('data-request') === requestPublicId) {
          requestCard = node;
          break;
        }
      }
    }
    const region = host.querySelector<HTMLElement>('[data-region="my-requests"]');
    const target = region ?? requestCard;
    if (!target) return;
    revealBelowPinnedChrome(target, { anchor: 0.15, host: this });
    if (typeof target.scrollIntoView !== 'function') return;
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    } catch {
      target.scrollIntoView();
    }
  }

  /** Set or clear one receipt field's error, keeping `aria-describedby` honest. */
  private markReceiptField(form: HTMLElement, control: HTMLElement, message: string | null): void {
    const errorId = `${control.id}-error`;
    const field = control.closest<HTMLElement>('.field') ?? form;
    let node = field.querySelector<HTMLElement>(`[id="${errorId}"]`);
    const describedBy = new Set((control.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean));
    if (message) {
      if (!node) {
        node = document.createElement('p');
        node.className = 'error';
        node.id = errorId;
        control.insertAdjacentElement('afterend', node);
      }
      node.textContent = message;
      control.setAttribute('aria-invalid', 'true');
      describedBy.add(errorId);
    } else {
      node?.remove();
      control.removeAttribute('aria-invalid');
      describedBy.delete(errorId);
    }
    if (describedBy.size > 0) control.setAttribute('aria-describedby', [...describedBy].join(' '));
    else control.removeAttribute('aria-describedby');
  }

  // ---- wiring -------------------------------------------------------------

  /**
   * Snapshot the optional body photos the customer picked.
   *
   * Called at submit time, before the flow can render the sign-in view and
   * destroy the controls. A `File` is not serializable, so it is held in memory:
   * it survives the in-page OTP round trip and is dropped only when the page
   * itself navigates (the Porabzar SSO handoff, where the fields are not shown).
   */
  private collectPhotoFiles(): void {
    if (!this.root || !this.definition) return;
    const picked: { kind: CustomerPhotoKind; file: File }[] = [];
    for (const field of this.definition.fields) {
      if (field.type !== 'file' || !field.photoKind) continue;
      const input = this.root.querySelector<HTMLInputElement>(
        `input[type="file"][name="${field.name}"]`,
      );
      const file = input?.files?.[0] ?? null;
      if (file) picked.push({ kind: field.photoKind, file });
    }
    this.pendingPhotoFiles = picked;
  }

  /**
   * Upload the picked body photos once the request exists.
   *
   * Keyed by request reference so the widget's click+submit double dispatch
   * (which the server absorbs with the idempotency key) uploads the set once.
   * A failure never hides the fact that the request was filed: it only changes
   * the notice the success view shows.
   */
  private formatAttachmentSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} بایت`;
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} کیلوبایت`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} مگابایت`;
  }

  private attachmentsPickerHtml(slug: string): string {
    const remaining = Math.max(0, MAX_REQUEST_ATTACHMENTS - this.pendingAttachmentFiles.length);
    const list =
      this.pendingAttachmentFiles.length > 0
        ? `<ul class="attachment-list" aria-label="پیوست‌های انتخاب‌شده">
            ${this.pendingAttachmentFiles
              .map(
                (file, index) => `<li class="attachment-item">
                  <span class="attachment-name">${esc(file.name)}</span>
                  <span class="attachment-size muted">${esc(this.formatAttachmentSize(file.size))}</span>
                  <button type="button" class="btn ghost attachment-remove" data-action="remove-attachment" data-index="${index}" aria-label="حذف ${esc(file.name)}">حذف</button>
                </li>`,
              )
              .join('')}
          </ul>`
        : '';
    const error = this.attachmentPickerError
      ? `<p class="error" role="alert" id="err-${esc(slug)}-attachments">${esc(this.attachmentPickerError)}</p>`
      : '';
    return `
      <fieldset class="attachments" data-attachments>
        <legend>پیوست‌های اختیاری</legend>
        <p class="help" id="help-${esc(slug)}-attachments">می‌توانید تا ${MAX_REQUEST_ATTACHMENTS} فایل تصویر یا PDF پیوست کنید؛ حداکثر حجم هر فایل ۵ مگابایت است.</p>
        ${list}
        ${
          remaining > 0
            ? `<div class="field">
                 <label for="f-${esc(slug)}-attachments">افزودن پیوست</label>
                 <input id="f-${esc(slug)}-attachments" name="attachments" type="file" accept="image/*,application/pdf" multiple data-action="pick-attachments" aria-describedby="help-${esc(slug)}-attachments${this.attachmentPickerError ? ` err-${esc(slug)}-attachments` : ''}" />
               </div>`
            : `<p class="help">حداکثر ${MAX_REQUEST_ATTACHMENTS} پیوست انتخاب شده است.</p>`
        }
        ${error}
      </fieldset>`;
  }

  private addAttachmentFiles(files: FileList | File[] | null | undefined): void {
    if (!files || files.length === 0) return;
    const next = [...this.pendingAttachmentFiles];
    let error: string | null = null;
    for (const file of Array.from(files)) {
      if (next.length >= MAX_REQUEST_ATTACHMENTS) {
        error = `حداکثر ${MAX_REQUEST_ATTACHMENTS} پیوست مجاز است.`;
        break;
      }
      if (file.size > MAX_REQUEST_ATTACHMENT_BYTES) {
        error = `حجم «${file.name}» بیش از ۵ مگابایت است.`;
        continue;
      }
      const duplicate = next.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified,
      );
      if (duplicate) continue;
      next.push(file);
    }
    this.pendingAttachmentFiles = next;
    this.attachmentPickerError = error;
    this.render(true);
  }

  private removeAttachmentAt(index: number): void {
    if (index < 0 || index >= this.pendingAttachmentFiles.length) return;
    this.pendingAttachmentFiles = this.pendingAttachmentFiles.filter((_, i) => i !== index);
    this.attachmentPickerError = null;
    this.render(true);
  }

  private uploadAttachmentsFor(result: CustomerRequestResultDto | null | undefined): Promise<void> {
    const reference = (result?.request?.publicId ?? '').trim();
    if (!this.client || !reference) return Promise.resolve();
    const running = this.attachmentUploads.get(reference);
    if (running) return running;
    if (this.pendingAttachmentFiles.length === 0) return Promise.resolve();

    const files = this.pendingAttachmentFiles;
    this.pendingAttachmentFiles = [];
    const run = (async (): Promise<void> => {
      const failures: string[] = [];
      for (const file of files) {
        try {
          await this.client!.requests.uploadAttachment(reference, { file });
        } catch (error) {
          const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
          failures.push(normalized.messageFa);
        }
      }
      this.attachmentUploads.delete(reference);
      this.attachmentNotice =
        failures.length > 0
          ? {
              tone: 'error',
              message: 'درخواست ثبت شد، اما بارگذاری بخشی از پیوست‌ها ناموفق بود.',
            }
          : { tone: 'ok', message: 'پیوست‌ها با موفقیت ثبت شد.' };
      this.announce(this.attachmentNotice.message);
      if (this.state.view === 'success') this.render(true);
    })();
    this.attachmentUploads.set(reference, run);
    return run;
  }

  private uploadPhotosFor(result: CustomerRequestResultDto | null | undefined): Promise<void> {
    const reference = (result?.request?.publicId ?? '').trim();
    if (!this.client || !reference) {
      return Promise.resolve();
    }
    // A second dispatch for the same request — the click/submit pair — joins the
    // upload already in flight instead of starting a second one.
    const running = this.photoUploads.get(reference);
    if (running) return running;
    if (this.pendingPhotoFiles.length === 0) {
      return Promise.resolve();
    }

    const files = this.pendingPhotoFiles;
    this.pendingPhotoFiles = [];
    const run = (async (): Promise<void> => {
      const failures: string[] = [];
      for (const { kind, file } of files) {
        try {
          await this.client!.requests.uploadPhoto(reference, { kind, file });
        } catch (error) {
          const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
          failures.push(normalized.messageFa);
        }
      }
      this.photoUploads.delete(reference);
      this.photoNotice =
        failures.length > 0
          ? {
              tone: 'error',
              message: 'درخواست ثبت شد، اما بارگذاری بخشی از تصاویر ناموفق بود.',
            }
          : { tone: 'ok', message: 'تصاویر بدن ثبت شد.' };
      this.announce(this.photoNotice.message);
      // The success view is already on screen; re-rendering it is what makes the
      // upload outcome visible, since the upload outlives the submit call.
      if (this.state.view === 'success') this.render(true);
    })();
    this.photoUploads.set(reference, run);
    return run;
  }

  private buildShadow(): void {
    this.root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = SERVICE_REQUEST_CSS;
    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('part', 'panel');
    const stage = document.createElement('div');
    stage.className = 'stage';
    stage.setAttribute('part', 'stage');
    const live = document.createElement('div');
    live.className = 'sr-only-live';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    const toastHost = document.createElement('div');
    toastHost.className = 'toast-host';
    toastHost.setAttribute('part', 'toast-host');
    const modalHost = document.createElement('div');
    modalHost.className = 'modal-host';
    modalHost.setAttribute('part', 'modal-host');
    panel.append(stage, live);
    this.root.append(style, panel, toastHost, modalHost);
    this.stage = stage;
    this.liveRegion = live;
    this.toastHost = toastHost;
    this.modalHost = modalHost;
    this.installDelegates();
  }

  private installDelegates(): void {
    if (!this.root) return;
    const onClick = (event: Event): void => {
      const clicked = event.target as HTMLElement | null;
      // The toast's close control is handled first: it must stay dismissable
      // even when the widget has no client or flow (the unconfigured state).
      if (clicked?.closest('[data-dismiss-toast]')) {
        this.hideReturnNotice();
        return;
      }
      if (clicked?.closest('.modal-host')) {
        if (this.myRequestsSheetOpen) {
          if (clicked.closest('[data-modal-close]') || clicked.classList.contains('modal-backdrop')) {
            this.closeMyRequestsSheet();
            return;
          }
          // Request actions inside the sheet fall through to data-action handling.
        } else if (this.cardTransfer) {
          if (clicked.closest('[data-modal-close]') || clicked.classList.contains('modal-backdrop')) {
            this.closeCardTransfer(true);
          } else if (clicked.closest('[data-modal-copy]')) {
            void this.copyCardNumber();
          } else if (clicked.closest('[data-modal-receipt]')) {
            void this.goToReceipt(this.cardTransfer.reference);
          }
          return;
        }
      }
      const target = clicked?.closest('[data-action]') as HTMLElement | null;
      if (!target || target.hasAttribute('disabled')) return;
      // A form's action runs only from its submit event. A tap inside the form
      // is the customer focusing or typing, and a submit button's click is
      // followed by that submit event — handling either here would send the
      // request early, or twice.
      if (target.tagName === 'FORM') return;
      if (target.tagName === 'BUTTON' && (target as HTMLButtonElement).type === 'submit' && (target as HTMLButtonElement).form) {
        return;
      }
      const action = target.getAttribute('data-action');
      void this.handleAction(action, target);
    };
    const onSubmit = (event: Event): void => {
      event.preventDefault();
      const form = event.target as HTMLElement;
      // The form is passed as the source so the receipt handler can read its
      // file and description without the widget ever storing a File in state.
      void this.handleAction(form.getAttribute('data-action') ?? 'submit', form);
    };
    const onInput = (event: Event): void => {
      const input = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      // The receipt form's controls are read from the DOM at submit time; they
      // must never leak into the request draft's answers.
      if (input.closest?.('[data-receipt]')) return;
      const name = input.getAttribute('name');
      if (!name || !this.flow) return;
      if (name === 'phone') {
        this.state.phone = input.value;
        return;
      }
      if (name === 'otp') {
        this.state.otp = input.value;
        return;
      }
      if (name === 'subject' || name === 'details') this.flow.setField(name, input.value);
      else if (input.type === 'checkbox')
        this.flow.setAnswer(name, (input as HTMLInputElement).checked ? 'true' : '');
      else if (input.type === 'file') return;
      else if (input.getAttribute('data-numeric') === '1')
        this.flow.setAnswer(name, normalizeDecimalInput(input.value));
      else this.flow.setAnswer(name, input.value);
      this.patchForm();
    };
    const onChange = (event: Event): void => {
      const input = event.target as HTMLInputElement;
      if (input?.getAttribute?.('data-action') === 'pick-attachments' && input.type === 'file') {
        this.addAttachmentFiles(input.files);
        input.value = '';
        return;
      }
      onInput(event);
    };
    const onKeydown = (event: Event): void => {
      const keyboard = event as KeyboardEvent;
      if (this.cardTransfer) {
        this.onDialogKeydown(keyboard);
        return;
      }
      const input = keyboard.target as HTMLInputElement;
      if (input?.getAttribute('name') === 'otp' && keyboard.key === 'Enter') {
        keyboard.preventDefault();
        void this.handleAction('verify-otp');
      }
    };

    this.root.addEventListener('click', onClick);
    this.root.addEventListener('submit', onSubmit);
    this.root.addEventListener('input', onInput);
    this.root.addEventListener('change', onChange);
    this.root.addEventListener('keydown', onKeydown);
    this.listeners.push(
      () => this.root?.removeEventListener('click', onClick),
      () => this.root?.removeEventListener('submit', onSubmit),
      () => this.root?.removeEventListener('input', onInput),
      () => this.root?.removeEventListener('change', onChange),
      () => this.root?.removeEventListener('keydown', onKeydown),
    );
    this.installAuthKeyboardGuards();
    this.installKeyboardChromeGuards();
  }

  /**
   * Hide the host page's pinned bars while the soft keyboard is open.
   *
   * All editable fields (phone, OTP/code, request form fields, receipt notes)
   * live inside this shadow root, so document-level focus listeners would only
   * see the host element. Listening here sees the real target. State is
   * published to `document.documentElement[data-keyboard]` (page chrome) and to
   * the host's own `data-keyboard` attribute; both transitions are CSS-only so
   * reduced-motion is honoured without JS checks. Focus drives the change and
   * the close is debounced, so rapid focus hops never flicker; visualViewport
   * only confirms a real keyboard and restores after dismissal, navigation and
   * validation reveals.
   */
  private installKeyboardChromeGuards(): void {
    if (typeof window === 'undefined' || !this.root) return;
    if (!this.keyboardChrome) {
      this.keyboardChrome = new KeyboardChromeController({
        onOpen: () => this.publishKeyboardState(),
        onClose: () => this.publishKeyboardState(),
      });
    }
    const chrome = this.keyboardChrome;
    let baseHeight = typeof window.innerHeight === 'number' ? window.innerHeight : 0;
    const shadowActive = (): Element | null => {
      try {
        return this.root?.activeElement ?? null;
      } catch {
        return null;
      }
    };
    const onFocusIn = (event: Event): void => {
      chrome.handleFocusIn(event.target);
      this.publishKeyboardState();
    };
    const onFocusOut = (event: Event): void => {
      const related = (event as FocusEvent).relatedTarget ?? null;
      // A re-render destroys the focused node (relatedTarget null *and* the
      // tracked node detached): that is not the customer leaving the field.
      const tracked = chrome.focusedElement;
      if (!related && tracked && typeof tracked.isConnected === 'boolean' && !tracked.isConnected) {
        chrome.handleNodeRemoval();
      } else {
        chrome.handleFocusOut(related);
      }
      this.publishKeyboardState();
    };
    const onViewport = (): void => {
      const visual = window.visualViewport;
      const current = visual?.height ?? window.innerHeight;
      chrome.handleViewport(baseHeight || current, current);
      // The focus signal is the primary trigger, but it must never be the
      // only one: if an editable field is focused right now, the bars stay
      // hidden regardless of which events arrived first.
      chrome.reconcileFocus(shadowActive());
      if (!visual || Math.abs(current - baseHeight) < 1) baseHeight = current;
      this.publishKeyboardState();
    };
    const onPageShow = (): void => {
      chrome.forceClose();
      this.publishKeyboardState();
      baseHeight = window.innerHeight;
    };
    const onResize = (): void => {
      if (!window.visualViewport) onViewport();
    };
    // `focusin` bubbles; `focus` does not, so it is caught on the way down.
    // Both feed the same state machine: whichever the browser delivers (or
    // both — the second is a no-op while open) hides the pinned bars.
    this.root.addEventListener('focusin', onFocusIn);
    this.root.addEventListener('focus', onFocusIn, true);
    this.root.addEventListener('focusout', onFocusOut);
    this.listeners.push(
      () => this.root?.removeEventListener('focusin', onFocusIn),
      () => this.root?.removeEventListener('focus', onFocusIn, true),
      () => this.root?.removeEventListener('focusout', onFocusOut),
    );
    const visual = window.visualViewport;
    if (visual) {
      visual.addEventListener('resize', onViewport);
      visual.addEventListener('scroll', onViewport);
      this.listeners.push(
        () => visual.removeEventListener('resize', onViewport),
        () => visual.removeEventListener('scroll', onViewport),
      );
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('pageshow', onPageShow);
    this.listeners.push(
      () => window.removeEventListener('resize', onResize),
      () => window.removeEventListener('pageshow', onPageShow),
    );
  }

  private publishKeyboardState(): void {
    const open = this.keyboardChrome?.isOpen ?? false;
    if (open) {
      this.setAttribute('data-keyboard', 'open');
    } else {
      this.removeAttribute('data-keyboard');
    }
    try {
      const root = typeof document !== 'undefined' ? document.documentElement : null;
      if (root) {
        if (open) root.setAttribute('data-keyboard', 'open');
        else root.removeAttribute('data-keyboard');
      }
    } catch {
      // Non-DOM test environments: host attribute is the source of truth.
    }
  }

  /**
   * Keep the focused field and its form's primary action above the soft
   * keyboard — once per focus interaction.
   *
   * A single delayed reveal after `focusin` covers the common case where the
   * soft keyboard resizes the visual viewport shortly after focus. Viewport
   * resize/scroll must not re-run the reveal: on iOS/Android those events fire
   * repeatedly while the keyboard animates (and while the customer scrolls),
   * and forcing `scrollTo` on each one shakes the page and steals manual
   * scroll. Focusing a different field starts a new one-shot interaction;
   * re-focusing the same field does not.
   */
  private installAuthKeyboardGuards(): void {
    if (typeof window === 'undefined' || !this.root) return;
    let revealedFor: Element | null = null;
    // DOM lib: window.setTimeout returns a number; Node typings use Timeout.
    let revealTimer: number | null = null;
    const onFocusIn = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.matches !== 'function' || !isEditableField(target)) return;
      if (revealedFor === target) return;
      revealedFor = target;
      if (revealTimer !== null) window.clearTimeout(revealTimer);
      // Soft keyboards often resize after focus settles; one shot is enough.
      revealTimer = window.setTimeout(() => {
        revealTimer = null;
        if (this.root?.activeElement === target) this.revealActiveAuthControls();
      }, 120);
    };
    const onFocusOut = (event: Event): void => {
      const related = (event as FocusEvent).relatedTarget;
      if (related && isEditableField(related)) return;
      revealedFor = null;
      if (revealTimer !== null) {
        window.clearTimeout(revealTimer);
        revealTimer = null;
      }
    };
    this.root.addEventListener('focusin', onFocusIn);
    this.root.addEventListener('focusout', onFocusOut);
    this.listeners.push(
      () => this.root?.removeEventListener('focusin', onFocusIn),
      () => this.root?.removeEventListener('focusout', onFocusOut),
      () => {
        if (revealTimer !== null) window.clearTimeout(revealTimer);
      },
    );
  }

  /**
   * Re-place the focused field and its primary action inside the visual
   * viewport band (below pinned chrome, above the soft keyboard).
   *
   * The primary action is the form's submit row (`.actions`, falling back to
   * the submit button itself): on the OTP step that is «تأیید و ادامه», on a
   * request form the submit row, on a receipt form its ثبت button. Anchoring
   * to the visual viewport band — rather than a fixed pixel offset — keeps
   * the action reachable at any viewport height and clear of safe-area insets,
   * which the layout viewport already accounts for.
   */
  private revealActiveAuthControls(): void {
    if (!this.root) return;
    const active = this.root.activeElement as HTMLElement | null;
    if (!active || typeof active.matches !== 'function' || !isEditableField(active)) return;
    const form = active.closest('form');
    const actions =
      form?.querySelector<HTMLElement>('.actions') ??
      (form?.querySelector<HTMLElement>('button[type="submit"]') as HTMLElement | null) ??
      null;
    // Prefer keeping the submit row reachable; only scroll when it is obscured.
    if (actions) {
      revealBelowPinnedChrome(actions, { anchor: 0.92, onlyIfObscured: true, host: this });
    }
    revealBelowPinnedChrome(active.closest('.field') ?? active, {
      anchor: AUTH_INPUT_ANCHOR,
      onlyIfObscured: true,
      host: this,
    });
  }

  private boot(): void {
    const resolved = this.resolveClientConfig();
    if (!resolved) {
      this.configProblem = 'پیکربندی این بخش کامل نیست.';
      this.state.view = 'unconfigured';
      this.render(true);
      return;
    }

    this.client = resolved.client;
    this.watchAuth();
    const slug = this.resolvedServiceSlug();
    if (!slug) {
      this.state.view = 'unknown-service';
      this.render(true);
      return;
    }
    this.definition = customerServiceDefinition(slug);
    this.applyTheme();
    // A return from the gateway carries `?payment=…&request=…`. It is consumed
    // once, as a toast, and then removed from the URL: replaying it on a later
    // render (or a refresh) could claim a success for a request that is not
    // actually paid.
    this.consumeGatewayReturn();
    // Google OIDC redirects back with `?auth=ok` (session), `?auth=phone`
    // (pending contact phone), or `?auth_error=…`. `?auth=login` opens the
    // sign-in chooser from the header. Params are read once so a refresh
    // cannot replay them or duplicate toasts.
    this.consumeGoogleAuthReturn();

    const store =
      this.getAttribute('persist-draft') === 'session' && resolved.config.authMode === 'sso'
        ? new SessionDraftStore(sessionStorageOrNull() ?? memoryStorage())
        : undefined;

    this.flow = new RequestFlow({
      client: this.client,
      service: slug,
      ...(store ? { store } : {}),
      onEvent: (name, detail) => {
        if (name === 'auth.required') {
          this.dispatchEvent(
            new CustomEvent(ELEMENT_EVENTS.authRequired, {
              bubbles: true,
              composed: true,
              detail: { service: slug, ...detail },
            }),
          );
        }
      },
    });
    this.flow.subscribe((snapshot) => this.onSnapshot(snapshot));

    // A back-navigation can restore this page from the bfcache without
    // re-running boot, so the request list would keep a pre-payment state. One
    // refresh on restore keeps the Pay button honest after the browser's back
    // button, which is one of the ways a customer returns from the gateway.
    if (typeof window !== 'undefined') {
      const onPageShow = (event: PageTransitionEvent): void => {
        if (event.persisted) void this.loadMyRequests();
      };
      window.addEventListener('pageshow', onPageShow);
      this.listeners.push(() => window.removeEventListener('pageshow', onPageShow));
      const onOpenMine = (): void => {
        this.openMyRequestsSheet();
      };
      window.addEventListener('tamoomeh:open-my-requests', onOpenMine);
      this.listeners.push(() => window.removeEventListener('tamoomeh:open-my-requests', onOpenMine));
    }

    // A draft persisted before a cross-domain sign-in is resumed here, and if it
    // was mid-submit the request is filed without the customer retyping anything.
    this.flow.restore();

    void this.client.auth.refresh().then(() => {
      this.dispatchEvent(new CustomEvent(ELEMENT_EVENTS.ready, { bubbles: true, composed: true, detail: { service: slug } }));
      void this.loadMyRequests();
      // Pending Google identity (cookie) must surface phone collection even
      // when the URL no longer carries `?auth=phone` (already consumed above).
      if (this.client?.auth.snapshot.needsPhoneVerification) {
        this.forceAuthView = true;
        this.enterAuth();
      }
      void this.maybeAutoResume();
      // Account menu «لیست درخواست‌های من» lands here with ?mine=1 when the host
      // page had no widget yet; open the sheet once and strip the param.
      this.consumeMineSheetParam();
    });
  }

  private consumeMineSheetParam(): void {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mine') !== '1') return;
      params.delete('mine');
      const next =
        window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', next);
      this.openMyRequestsSheet();
    } catch {
      // History APIs can throw in sandboxed tests; ignore.
    }
  }

  /**
   * Resolve the client for this element.
   *
   * A host bootstrap may already have created one (`configureClient`), in which
   * case every element shares it — one session probe per page, not one per
   * widget. Otherwise the config is read from the document.
   */
  private resolveClientConfig(): { client: TamoomehClient; config: ResolvedCustomerClientConfig } | null {
    if (sharedClient) return { client: sharedClient, config: sharedClient.config };
    const fromDocument = readDocumentConfig();
    if (!fromDocument) return null;
    sharedClient = createTamoomehClient(fromDocument);
    return { client: sharedClient, config: sharedClient.config };
  }

  private resolvedServiceSlug(): CustomerServiceSlug | null {
    const config = this.client?.config ?? sharedClient?.config ?? readDocumentConfig();
    if (!config) return null;
    const normalized = 'services' in config && Array.isArray(config.services) ? config : normalizeConfig(config);
    const { slug } = resolveWidgetService(normalized as ResolvedCustomerClientConfig, this.getAttribute('service'));
    return slug;
  }

  private applyTheme(): void {
    if (!this.definition) return;
    this.setAttribute('style', themeStyleFor(this.definition));
    const appearance = this.getAttribute('appearance');
    if (!appearance || !(APPEARANCE_MODES as readonly string[]).includes(appearance)) {
      this.setAttribute('appearance', 'neutral');
    }
  }

  private onSnapshot(snapshot: RequestFlowSnapshot): void {
    if (this.forceAuthView) {
      this.state.view = 'auth';
      this.render(false, snapshot);
      return;
    }
    this.state.view = this.viewFor(snapshot);
    this.render(false, snapshot);
  }

  private viewFor(snapshot: RequestFlowSnapshot): ViewKey {
    switch (snapshot.status) {
      case 'authenticating':
        return 'auth';
      case 'submitting':
        return 'submitting';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'form';
    }
  }

  // ---- rendering ----------------------------------------------------------

  private render(force: boolean, snapshot?: RequestFlowSnapshot): void {
    if (!this.stage) return;
    const current = snapshot ?? this.flow?.snapshot ?? null;
    const view = this.state.view;
    const viewChanged = force || this.lastView !== view;

    if (viewChanged) {
      this.lastView = view;
      this.stage.innerHTML = this.viewHtml(view, current);
      this.applyViewFixes(view);
    } else if (view === 'form' && current) {
      this.patchForm();
    }

    if (this.liveRegion && this.state.announcement) {
      this.liveRegion.textContent = this.state.announcement;
    }
  }

  private announce(message: string): void {
    this.state.announcement = message;
    if (this.liveRegion) this.liveRegion.textContent = message;
  }

  private viewHtml(view: ViewKey, snapshot: RequestFlowSnapshot | null): string {
    switch (view) {
      case 'unconfigured':
        return this.statusHtml(
          'error',
          'این بخش آماده نیست',
          this.configProblem ?? 'پیکربندی سرویس درخواست کامل نیست.',
        );
      case 'unknown-service':
        return this.statusHtml(
          'error',
          'بخش نامشخص',
          'این نوع درخواست شناخته نشد. لطفاً از صفحه اصلی یکی از بخش‌ها وارد شوید.',
        );
      case 'auth':
        return this.authHtml();
      case 'submitting':
        return this.statusHtml(
          'info',
          'در حال ثبت درخواست…',
          'لطفاً صفحه را نبندید. این کار چند لحظه طول می‌کشد.',
          '<span class="spinner" aria-hidden="true"></span>',
        );
      case 'success':
        return this.successHtml(snapshot);
      case 'error':
        return this.errorHtml(snapshot);
      default:
        return this.formHtml(snapshot);
    }
  }

  /**
   * Read the gateway's return parameters, show them once, then drop them.
   *
   * The message is a toast rather than a paragraph in the flow: it describes
   * something that happened *outside* the widget (a redirect back from the
   * gateway), so it must not sit under whichever request happens to be on
   * screen. Consuming the query params is what makes the notice one-shot —
   * a refresh, a re-render, or a fresh request can never resurrect it.
   */
  private consumeGatewayReturn(): void {
    const notice = gatewayReturnNotice();
    if (!notice) return;
    this.showReturnNotice(notice);
    clearGatewayReturnParams();
  }

  /**
   * Open phone collection after Google, open the login chooser, toast success,
   * or show an error.
   *
   * Core's callback redirects with `?auth=phone` when a contact phone is still
   * required (no SMS OTP), `?auth=ok` after a full session is issued, and
   * `?auth=login` from the header menu. `?auth_error=…` covers Google cancel /
   * validation failure. Params are stripped immediately so a refresh cannot
   * replay them or duplicate toasts.
   */
  private consumeGoogleAuthReturn(): void {
    const outcome = googleAuthReturn(typeof location === 'undefined' ? '' : location.search);
    clearGoogleAuthReturnParams();
    if (!outcome) return;
    if (outcome.needsPhone) {
      this.forceAuthView = true;
      this.state.view = 'auth';
      this.state.authStep = 'phone';
      this.authBusy = null;
    } else if (outcome.openLogin) {
      this.forceAuthView = true;
      this.state.view = 'auth';
      this.state.authStep = 'choose';
      this.authBusy = null;
    }
    if (outcome.errorMessage) {
      this.showReturnNotice({ tone: 'error', message: outcome.errorMessage });
      return;
    }
    if (outcome.loginSuccess) {
      this.showReturnNotice({ tone: 'success', message: LOGIN_SUCCESS_TOAST_FA });
    }
  }

  private showReturnNotice(notice: ReturnNotice): void {
    this.announce(notice.message);
    const host = this.toastHost;
    if (!host) return;
    const role = notice.tone === 'error' ? 'alert' : 'status';
    host.innerHTML = `
      <div class="pay-toast" data-tone="${notice.tone}" role="${role}" part="toast">
        <span class="pay-toast-dot" aria-hidden="true"></span>
        <strong class="pay-toast-message">${esc(notice.message)}</strong>
        <button class="pay-toast-close" type="button" data-dismiss-toast aria-label="بستن پیام">×</button>
      </div>`;
    // Auth/request success must stay long enough to read; errors stay longer.
    this.clearReturnNoticeTimer();
    const dwellMs =
      notice.tone === 'error'
        ? 12_000
        : notice.message === LOGIN_SUCCESS_TOAST_FA || notice.message === REQUEST_SUCCESS_TOAST_FA
          ? 12_000
          : 8_000;
    this.returnNoticeTimer = setTimeout(() => this.hideReturnNotice(), dwellMs);
  }

  /** Dismiss the toast and forget it, so no later view can re-show it. */
  private hideReturnNotice(): void {
    this.clearReturnNoticeTimer();
    const host = this.toastHost;
    const node = host?.querySelector<HTMLElement>('.pay-toast');
    if (!host || !node) {
      if (host) host.innerHTML = '';
      return;
    }
    node.setAttribute('data-leaving', '1');
    setTimeout(() => node.remove(), 200);
  }

  private clearReturnNoticeTimer(): void {
    if (this.returnNoticeTimer === null) return;
    clearTimeout(this.returnNoticeTimer);
    this.returnNoticeTimer = null;
  }

  private headerHtml(): string {
    const definition = this.definition;
    if (!definition) return '';
    const heading = this.getAttribute('heading') ?? definition.requestTitleFa;
    const compact = this.hasAttribute('compact');
    return `
      <div class="head">
        <span class="icon" part="icon">${iconSvg(definition.icon)}</span>
        <div class="titles">
          <h2 part="heading">${esc(heading)}</h2>
          ${compact ? '' : `<p>${esc(definition.summaryFa)}</p>`}
        </div>
      </div>`;
  }

  private statusHtml(
    tone: 'info' | 'error' | 'success',
    title: string,
    body: string,
    extra = '',
  ): string {
    return `
      <div class="status" data-tone="${tone}" part="status">
        <span class="dot" aria-hidden="true"></span>
        <div class="body">
          <strong>${esc(title)}</strong>
          <p>${esc(body)}</p>
        </div>
        ${extra}
      </div>`;
  }

  private formHtml(snapshot: RequestFlowSnapshot | null): string {
    const definition = this.definition;
    if (!definition) return '';
    const draft = snapshot?.draft ?? { subject: '', details: '', answers: {} };
    const issues = snapshot?.issues ?? [];
    const issueFor = (field: string) => issues.find((issue) => issue.field === field);

    const canUploadPhotos = this.client?.requests.photoUploadAvailable ?? false;
    // The two universal fields live on the draft root, not in `answers`; every
    // service-specific field lives in `answers`. One accessor keeps both shapes
    // straight for every control type.
    const valueOf = (fieldName: string): string =>
      fieldName === 'subject'
        ? draft.subject
        : fieldName === 'details'
          ? draft.details
          : (draft.answers[fieldName] ?? '');
    const controls = definition.fields
      .filter((field) => field.type !== 'file' || canUploadPhotos)
      .map((field) => {
        const issue = issueFor(field.name);
        const errorId = `err-${definition.slug}-${field.name}`;
        const helpId = `help-${definition.slug}-${field.name}`;
        const describedBy = [field.helpFa ? helpId : null, issue ? errorId : null]
          .filter(Boolean)
          .join(' ');

        // A checkbox is not a labelled input stacked above a control: the label
        // belongs beside the box and carries the whole click target. Rendering
        // it through the ordinary field shape would put a label above a checkbox
        // and a stray `class="control"` on it.
        if (field.type === 'checkbox') {
          const checked = (draft.answers[field.name] ?? '') === 'true';
          return `
          <div class="field field-check">
            <label class="check" for="f-${definition.slug}-${field.name}">
              <input id="f-${definition.slug}-${field.name}" name="${esc(field.name)}" type="checkbox" value="true"${
                checked ? ' checked' : ''
              }${issue ? ' aria-invalid="true"' : ''}${
                describedBy ? ` aria-describedby="${describedBy}"` : ''
              } />
              <span>${esc(field.labelFa)}</span>
            </label>
            ${field.helpFa ? `<p class="help" id="${helpId}">${esc(field.helpFa)}</p>` : ''}
            ${issue ? `<p class="error" id="${errorId}">${esc(issue.messageFa)}</p>` : ''}
          </div>`;
        }

        const common = [
          `id="f-${definition.slug}-${field.name}"`,
          `name="${esc(field.name)}"`,
          `class="control"`,
          field.required ? 'required aria-required="true"' : '',
          issue ? 'aria-invalid="true"' : '',
          describedBy ? `aria-describedby="${describedBy}"` : '',
          field.maxLength ? `maxlength="${field.maxLength}"` : '',
        ]
          .filter(Boolean)
          .join(' ');

        let control: string;
        if (field.type === 'textarea') {
          control = `<textarea ${common} rows="5" placeholder="${esc(field.placeholderFa ?? '')}">${esc(valueOf(field.name))}</textarea>`;
        } else if (field.type === 'select') {
          const selected = valueOf(field.name);
          const options = (field.options ?? [])
            .map(
              (option) =>
                `<option value="${esc(option.value)}"${option.value === selected ? ' selected' : ''}>${esc(option.labelFa)}</option>`,
            )
            .join('');
          control = `<select ${common}><option value="">— انتخاب کنید —</option>${options}</select>`;
        } else if (field.type === 'number') {
          // `type="text"` + `inputmode="decimal"` so Persian/Arabic digits can be
          // typed (native `type="number"` rejects them). Bounds are enforced by
          // the shared validator after `normalizeDecimalInput`, not by the browser.
          const value = valueOf(field.name);
          control = `<input ${common} type="text" inputmode="decimal" data-numeric="1" dir="ltr" autocomplete="off" value="${esc(value)}" placeholder="${esc(field.placeholderFa ?? '')}" />`;
        } else if (field.type === 'file') {
          // A file input has no value to restore: the customer re-picks after a
          // sign-in that crossed a full page load, and the field says so.
          control = `<input ${common} type="file" accept="${esc(field.accept ?? 'image/*')}" />`;
        } else {
          control = `<input ${common} type="text" value="${esc(valueOf(field.name))}" placeholder="${esc(field.placeholderFa ?? '')}" />`;
        }

        return `
          <div class="field">
            <label for="f-${definition.slug}-${field.name}">${esc(field.labelFa)}${field.required ? '<span class="req" aria-hidden="true">*</span>' : ''}</label>
            ${control}
            ${field.helpFa ? `<p class="help" id="${helpId}">${esc(field.helpFa)}</p>` : ''}
            ${issue ? `<p class="error" id="${errorId}">${esc(issue.messageFa)}</p>` : ''}
          </div>`;
      })
      .join('');

    const submitLabel = definition.ctaFa;
    const attachmentsBlock =
      this.client?.requests.attachmentUploadAvailable !== false
        ? this.attachmentsPickerHtml(definition.slug)
        : '';
    return `
      ${this.headerHtml()}
      <form class="form" data-action="submit" novalidate>
        ${controls}
        ${attachmentsBlock}
        <div class="actions">
          <button class="btn" type="submit" part="submit" data-action="submit">${esc(submitLabel)}</button>
        </div>
        <p class="meta">با ثبت درخواست، کارشناس مربوطه بررسی را آغاز می‌کند.</p>
      </form>
      <section class="mine" part="my-requests" aria-labelledby="mine-${definition.slug}-title" data-region="my-requests">
        ${this.myRequestsHtml({ scope: 'section' })}
      </section>`;
  }

  private authHtml(): string {
    const definition = this.definition;
    if (!definition || !this.client) return '';
    const mode = this.client.config.authMode;

    if (mode === 'sso') {
      return `
        ${this.headerHtml()}
        <div class="status" data-tone="info" part="status">
          <span class="dot" aria-hidden="true"></span>
          <div class="body">
            <strong>ورود با شماره موبایل</strong>
            <p>برای ثبت درخواست، یک‌بار با شماره موبایل وارد می‌شوید. اطلاعاتی که نوشته‌اید حفظ می‌شود.</p>
          </div>
        </div>
        <div class="actions">
          <button class="btn" type="button" data-action="sso-login">${iconSvg('lock', 18)} ورود و ادامه</button>
          <button class="btn ghost" type="button" data-action="cancel-auth">بازگشت</button>
        </div>`;
    }

    const needsPhone = this.client.auth.snapshot.needsPhoneVerification;
    const googleEnabled = this.client.auth.snapshot.authMethods.google;
    const step =
      this.state.authStep === 'choose' && (!googleEnabled || needsPhone) ? 'phone' : this.state.authStep;

    if (step === 'choose') {
      const googleBusy = this.authBusy === 'google-login';
      return `
        ${this.headerHtml()}
        <div class="auth-chooser" part="auth-chooser" role="group" aria-label="روش ورود">
          <p class="auth-chooser-lead">یکی از روش‌های زیر را انتخاب کنید</p>
          <div class="auth-method-picker">
            <button class="btn auth-method auth-method-google" type="button"
                    data-action="google-login"${googleBusy ? ' disabled aria-busy="true"' : ''}>
              ${googleBusy ? '<span class="spinner" aria-hidden="true"></span>' : iconSvg('google', 20)}
              <span class="auth-method-copy">
                <strong>ورود با ایمیل (گوگل)</strong>
                <span>ورود امن با حساب ایمیل گوگل</span>
              </span>
            </button>
            <div class="auth-method-or" role="separator" aria-label="یا">
              <span>یا</span>
            </div>
            <button class="btn secondary auth-method auth-method-sms" type="button" data-action="auth-sms">
              ${iconSvg('sms', 20)}
              <span class="auth-method-copy">
                <strong>ورود با شماره موبایل</strong>
                <span>ارسال کد تأیید پیامکی</span>
              </span>
            </button>
          </div>
          <div class="actions auth-chooser-back">
            <button class="btn ghost" type="button" data-action="cancel-auth">بازگشت</button>
          </div>
        </div>`;
    }

    if (step === 'phone') {
      // Google pending → contact collection only (no SMS OTP). Ordinary SMS
      // login still uses the OTP request path below.
      if (needsPhone) {
        const submitBusy = this.authBusy === 'submit-google-phone';
        return `
        ${this.headerHtml()}
        <form class="form auth-phone-box" data-action="submit-google-phone" novalidate>
          <div class="auth-phone-toolbar">
            <button class="btn ghost auth-back" type="button" data-action="cancel-auth"${
              submitBusy ? ' disabled' : ''
            }>بازگشت</button>
          </div>
          <h3 class="auth-phone-heading">تکمیل پروفایل</h3>
          <p class="help auth-phone-lead" id="help-${definition.slug}-phone">برای تماس و پیگیری درخواست، شماره موبایل اعلان خود را وارد کنید.</p>
          <div class="field">
            <label for="f-${definition.slug}-phone">شماره موبایل<span class="req" aria-hidden="true">*</span></label>
            <div class="phone-row">
              <input id="f-${definition.slug}-phone" class="control" name="phone" type="tel"
                     inputmode="tel" autocomplete="tel" dir="ltr" required
                     value="${esc(this.state.phone)}" placeholder="0912 345 6789"
                     aria-describedby="help-${definition.slug}-phone"
                     ${submitBusy ? 'readonly' : ''} />
            </div>
          </div>
          <div class="actions">
            <button class="btn" type="submit" data-action="submit-google-phone"${
              submitBusy ? ' disabled aria-busy="true"' : ''
            }>${submitBusy ? '<span class="spinner" aria-hidden="true"></span> در حال ارسال…' : 'تکمیل پروفایل'}</button>
          </div>
        </form>`;
      }

      const requestBusy = this.authBusy === 'request-otp';
      return `
        ${this.headerHtml()}
        <form class="form auth-phone-box" data-action="request-otp" novalidate>
          <div class="auth-phone-toolbar">
            <button class="btn ghost auth-back" type="button" data-action="${
              googleEnabled ? 'auth-back-chooser' : 'cancel-auth'
            }">بازگشت</button>
          </div>
          <div class="field">
            <label for="f-${definition.slug}-phone">شماره موبایل<span class="req" aria-hidden="true">*</span></label>
            <div class="phone-row">
              <input id="f-${definition.slug}-phone" class="control" name="phone" type="tel"
                     inputmode="tel" autocomplete="tel" dir="ltr" required
                     value="${esc(this.state.phone)}" placeholder="0912 345 6789"
                     aria-describedby="help-${definition.slug}-phone"
                     ${requestBusy ? 'readonly' : ''} />
            </div>
            <p class="help" id="help-${definition.slug}-phone">کد تأیید به این شماره پیامک می‌شود.</p>
          </div>
          <div class="actions">
            <button class="btn" type="submit" data-action="request-otp"${
              requestBusy ? ' disabled aria-busy="true"' : ''
            }>${requestBusy ? '<span class="spinner" aria-hidden="true"></span> در حال ارسال…' : 'ارسال کد تأیید پیامکی'}</button>
          </div>
        </form>`;
    }

    const challenge = this.client.auth.snapshot.pendingChallenge;
    const verifyBusy = this.authBusy === 'verify-otp';
    const resendBusy = this.authBusy === 'request-otp';
    return `
      ${this.headerHtml()}
      <form class="form" data-action="verify-otp" novalidate>
        <div class="field otp">
          <label for="f-${definition.slug}-otp">کد تأیید</label>
          <input id="f-${definition.slug}-otp" name="otp" type="text" inputmode="numeric"
                 autocomplete="one-time-code" maxlength="8" dir="ltr"
                 value="${esc(this.state.otp)}" aria-describedby="help-${definition.slug}-otp"
                 ${verifyBusy || resendBusy ? 'readonly' : ''} />
          <p class="hint" id="help-${definition.slug}-otp">کد پیامک‌شده به <span class="phone-display" dir="ltr">${esc(formatIranMobile(challenge?.phone ?? this.state.phone))}</span> را وارد کنید.</p>
          ${challenge?.devCode ? `<p class="hint">کد آزمایشی: <span dir="ltr">${esc(challenge.devCode)}</span></p>` : ''}
        </div>
        <div class="actions">
          <button class="btn" type="submit" data-action="verify-otp"${
            verifyBusy || resendBusy ? ' disabled aria-busy="true"' : ''
          }>${verifyBusy ? '<span class="spinner" aria-hidden="true"></span> در حال تأیید…' : 'تأیید و ادامه'}</button>
          <button class="btn secondary" type="button" data-action="request-otp"${
            verifyBusy || resendBusy ? ' disabled aria-busy="true"' : ''
          }>${resendBusy ? '<span class="spinner" aria-hidden="true"></span> در حال ارسال…' : 'ارسال دوباره کد'}</button>
          <button class="btn ghost" type="button" data-action="change-phone"${
            verifyBusy || resendBusy ? ' disabled' : ''
          }>تغییر شماره</button>
        </div>
      </form>`;
  }

  private successHtml(snapshot: RequestFlowSnapshot | null): string {
    const result = snapshot?.result;
    const request = result?.request;
    const definition = this.definition;
    // The receipt shows the customer-facing stage, never the raw backend status:
    // the whole point of the lifecycle mapping is that `AWAITING_PAYMENT` never
    // reaches a customer's screen.
    const stage = definition ? customerStageFor(definition.slug, request?.status) : null;
    const stageTitle = customerStageDefinition(stage)?.titleFa ?? 'در حال به‌روزرسانی وضعیت';
    const reference = request?.publicId ?? '';
    const canPay =
      result?.canPay === true && reference !== '' && (this.client?.payments.available ?? false);
    const paying = this.payingRequest === reference;
    const payError = this.paymentError?.reference === reference ? this.paymentError.message : null;
    // Show the payable figure on the confirmation whenever money is still owed
    // for this request, so the customer sees the amount before they pay.
    const owesMoney = canPay || result?.canSubmitReceipt === true;
    const price = owesMoney ? this.checkoutPriceHtml('success') : '';
    const receiptArea = request
      ? this.receiptAreaHtml(
          request.publicId,
          result?.canSubmitReceipt,
          result?.receiptBlockedReason,
        )
      : '';
    return `
      ${this.headerHtml()}
      <div class="status" data-tone="success" part="status">
        <span class="dot" aria-hidden="true"></span>
        <div class="body">
          <strong>درخواست شما ثبت شد</strong>
          <p>${
            canPay
              ? 'برای ادامه، پرداخت این درخواست را تکمیل کنید.'
              : `کارشناس ${esc(definition?.nameFa ?? '')} درخواست را بررسی می‌کند و با شما تماس می‌گیرد.`
          }</p>
        </div>
      </div>
      ${price}
      ${
        request
          ? `<dl class="receipt">
              <div class="row"><dt>کد پیگیری</dt><dd class="code">${esc(request.publicId)}</dd></div>
              <div class="row"><dt>موضوع</dt><dd>${esc(request.title ?? '')}</dd></div>
              <div class="row"><dt>وضعیت</dt><dd>${esc(stageTitle)}</dd></div>
            </dl>`
          : ''
      }
      <div class="actions">
        ${
          canPay
            ? `<button class="btn pay" type="button" data-action="pay" data-request="${esc(reference)}"${
                paying ? ' disabled aria-busy="true"' : ''
              }>
                ${iconSvg('card', 18)} پرداخت ${esc(priceSummaryFor(this.definition)?.saleFa ?? '')}
              </button>`
            : ''
        }
        <button class="btn secondary" type="button" data-action="new-request">ثبت درخواست جدید</button>
      </div>
      ${payError ? `<p class="error" role="alert">${esc(payError)}</p>` : ''}
      ${receiptArea}
      ${
        this.photoNotice
          ? `<p class="${this.photoNotice.tone === 'error' ? 'error' : 'req-hint'}" role="${
              this.photoNotice.tone === 'error' ? 'alert' : 'status'
            }">${esc(this.photoNotice.message)}</p>`
          : ''
      }
      ${
        this.attachmentNotice
          ? `<p class="${this.attachmentNotice.tone === 'error' ? 'error' : 'req-hint'}" role="${
              this.attachmentNotice.tone === 'error' ? 'alert' : 'status'
            }">${esc(this.attachmentNotice.message)}</p>`
          : ''
      }
      <section class="mine" part="my-requests" aria-labelledby="mine-${esc(definition?.slug ?? '')}-title" data-region="my-requests">
        ${this.myRequestsHtml({ scope: 'section' })}
      </section>`;
  }

  private errorHtml(snapshot: RequestFlowSnapshot | null): string {
    const error = snapshot?.error;
    return `
      ${this.headerHtml()}
      ${this.statusHtml(
        'error',
        'ثبت درخواست انجام نشد',
        error?.messageFa ?? 'خطای نامشخص رخ داد.',
      )}
      <div class="actions">
        <button class="btn" type="button" data-action="retry">${iconSvg('refresh', 18)} تلاش دوباره</button>
      </div>
      <p class="meta">اطلاعاتی که نوشته‌اید پاک نشده است.</p>`;
  }

  /** Move focus to the element a customer should interact with next. */
  private applyViewFixes(view: ViewKey): void {
    if (!this.root) return;
    const focusables: Partial<Record<ViewKey, string>> = {
      auth: 'input[name="phone"], input[name="otp"], button[data-action="google-login"], button[data-action="auth-sms"], button[data-action="sso-login"]',
      success: 'button[data-action="pay"], button[data-action="new-request"]',
      error: 'button[data-action="retry"]',
    };
    const selector = focusables[view];
    if (selector) {
      const target = this.root.querySelector<HTMLElement>(selector);
      // Only for a view the customer just arrived at, never during the initial
      // page render. The placement does not wait for window focus (only the
      // focus itself does): a form left scrolled past its new, shorter view
      // would hide the input above the viewport.
      if (target && this.lastView !== null) {
        focusWithoutScroll(target);
        // The phone and code inputs are placed, not merely made visible: on a
        // sports page the form would otherwise slide under the pinned service
        // strip. Buttons only need to be on screen.
        const isAuthInput = target.matches('input');
        revealBelowPinnedChrome(target, {
          anchor: isAuthInput ? AUTH_INPUT_ANCHOR : 0.5,
          onlyIfObscured: !isAuthInput,
          host: this,
        });
      }
    }
    if (view === 'submitting') this.announce('در حال ثبت درخواست');
    if (view === 'success') this.announce('درخواست شما ثبت شد');
  }

  /**
   * Update the parts of the form that change while typing.
   *
   * Deliberately *not* a re-render: replacing `innerHTML` on every keystroke
   * would destroy the caret and the focus, which is the single most common way a
   * Web Component form becomes unusable.
   */
  private patchForm(): void {
    if (!this.root || !this.flow || !this.definition) return;
    const snapshot = this.flow.snapshot;
    const form = this.root.querySelector<HTMLFormElement>('form[data-action="submit"]');
    if (!form) return;

    for (const field of this.definition.fields) {
      const control = form.querySelector<HTMLElement>(`[name="${field.name}"]`);
      if (!control) continue;
      const issue = snapshot.issues.find((candidate) => candidate.field === field.name);
      const errorId = `err-${this.definition.slug}-${field.name}`;
      const helpId = `help-${this.definition.slug}-${field.name}`;
      const existing = form.querySelector<HTMLElement>(`#${errorId}`);
      // `aria-describedby` has to be maintained here as well as in the initial
      // markup. Setting `aria-invalid` alone tells a screen reader that the
      // field is wrong without telling it *why*, which is the difference
      // between an accessible error and a red border.
      const describedBy = [
        form.querySelector(`#${helpId}`) ? helpId : null,
        issue ? errorId : null,
      ].filter(Boolean) as string[];

      if (issue) {
        control.setAttribute('aria-invalid', 'true');
        if (existing) {
          existing.textContent = issue.messageFa;
        } else {
          const node = document.createElement('p');
          node.className = 'error';
          node.id = errorId;
          node.textContent = issue.messageFa;
          control.insertAdjacentElement('afterend', node);
        }
      } else {
        control.removeAttribute('aria-invalid');
        existing?.remove();
      }

      if (describedBy.length > 0) control.setAttribute('aria-describedby', describedBy.join(' '));
      else control.removeAttribute('aria-describedby');
    }

    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) {
      submit.disabled = snapshot.status === 'submitting';
      submit.setAttribute('aria-busy', String(snapshot.status === 'submitting'));
    }
  }

  // ---- behaviour ----------------------------------------------------------

  private async handleAction(action: string | null, source?: HTMLElement | null): Promise<void> {
    if (!this.client || !this.flow) return;
    const auth = this.client.auth;

    switch (action) {
      case 'remove-attachment': {
        const index = Number(source?.getAttribute('data-index') ?? -1);
        this.removeAttachmentAt(index);
        return;
      }
      case 'submit': {
        // The photos are read from the controls before the flow can render the
        // sign-in view and take the file inputs with it. Attachments are already
        // held in `pendingAttachmentFiles` from the picker.
        this.collectPhotoFiles();
        const outcome = await this.flow.submit();
        if (outcome.needsAuth) {
          this.state.authStep = 'choose';
          this.enterAuth();
        } else if (outcome.result) {
          // Only after the backend confirmed creation — never earlier.
          this.showReturnNotice({ tone: 'success', message: REQUEST_SUCCESS_TOAST_FA });
          // Not awaited: the request exists now, and the success view must not
          // wait on optional photo/attachment bytes.
          void this.uploadPhotosFor(outcome.result);
          void this.uploadAttachmentsFor(outcome.result);
          this.dispatchEvent(
            new CustomEvent(ELEMENT_EVENTS.requestSuccess, {
              bubbles: true,
              composed: true,
              detail: { service: this.flow.currentService, request: outcome.result.request },
            }),
          );
          // The request the customer just filed belongs in their list, so the
          // list is refreshed rather than left showing the pre-submit snapshot.
          void this.loadMyRequests();
        } else if (outcome.error) {
          if (outcome.error.code === 'VALIDATION_ERROR') this.revealFirstInvalidField(this.requestForm());
          this.dispatchEvent(
            new CustomEvent(ELEMENT_EVENTS.error, {
              bubbles: true,
              composed: true,
              detail: { service: this.flow.currentService, code: outcome.error.code },
            }),
          );
        }
        return;
      }

      case 'auth-sms':
        this.state.authStep = 'phone';
        this.authBusy = null;
        this.render(true);
        return;

      case 'auth-back-chooser':
        // Leave the phone form; do not clear a pending Google cookie — that must
        // not become a session, and the customer may still need to supply a phone.
        this.state.authStep = 'choose';
        this.authBusy = null;
        this.state.otp = '';
        this.render(true);
        return;

      case 'submit-google-phone': {
        if (this.authBusy) return;
        const phone = this.state.phone.trim();
        if (!isValidIranMobile(phone)) {
          this.reportAuthError(new CustomerApiError('VALIDATION_ERROR', INVALID_PHONE_MESSAGE_FA));
          return;
        }
        this.authBusy = 'submit-google-phone';
        this.render(true);
        try {
          await auth.submitGooglePhone(phone);
          this.forceAuthView = false;
          this.authBusy = null;
          this.showReturnNotice({ tone: 'success', message: LOGIN_SUCCESS_TOAST_FA });
          await this.maybeAutoResume(true);
          await this.loadMyRequests();
        } catch (error) {
          this.authBusy = null;
          this.render(true);
          this.reportAuthError(phoneRejection(error) ?? error);
        }
        return;
      }

      case 'request-otp': {
        if (this.authBusy) return;
        // Prefer the typed number; on resend fall back to the challenge phone
        // (E.164) so an empty field cannot silently no-op the button.
        const phone = (
          this.state.phone.trim() ||
          auth.snapshot.pendingChallenge?.phone ||
          ''
        ).trim();
        if (!isValidIranMobile(phone)) {
          this.reportAuthError(new CustomerApiError('VALIDATION_ERROR', INVALID_PHONE_MESSAGE_FA));
          return;
        }
        if (!this.state.phone.trim()) this.state.phone = phone;
        this.authBusy = 'request-otp';
        this.render(true);
        try {
          await auth.requestOtp(phone);
          this.state.authStep = 'otp';
          this.state.otp = '';
          this.authBusy = null;
          this.announce('کد تأیید ارسال شد');
          this.render(true);
        } catch (error) {
          // Clear busy and re-render *before* attaching the error node so a
          // later full render cannot wipe the message.
          this.authBusy = null;
          this.render(true);
          this.reportAuthError(phoneRejection(error) ?? error);
        }
        return;
      }

      case 'verify-otp': {
        if (this.authBusy) return;
        this.authBusy = 'verify-otp';
        this.render(true);
        try {
          const phone = (
            this.state.phone.trim() ||
            auth.snapshot.pendingChallenge?.phone ||
            ''
          ).trim();
          await auth.verifyOtp({
            phone,
            code: this.state.otp.trim(),
            ...(auth.snapshot.pendingChallenge?.challengeId
              ? { challengeId: auth.snapshot.pendingChallenge.challengeId }
              : {}),
          });
          this.forceAuthView = false;
          this.authBusy = null;
          this.showReturnNotice({ tone: 'success', message: LOGIN_SUCCESS_TOAST_FA });
          await this.maybeAutoResume(true);
          await this.loadMyRequests();
        } catch (error) {
          this.authBusy = null;
          this.render(true);
          this.reportAuthError(error);
        }
        return;
      }

      case 'sso-login': {
        // Persist first: the next thing that happens is a full page navigation.
        this.flow.persist(true);
        const url = auth.beginLogin({
          service: this.flow.currentService,
          returnTo: currentLocation(),
        });
        navigateTo(url);
        return;
      }

      case 'google-login': {
        if (this.authBusy) return;
        this.flow.persist(true);
        this.authBusy = 'google-login';
        this.render(true);
        try {
          const url = auth.beginGoogleLogin({
            service: this.flow.currentService,
            returnTo: currentLocation(),
          });
          navigateTo(url);
        } catch (error) {
          this.authBusy = null;
          this.reportAuthError(error);
        }
        return;
      }

      case 'change-phone':
        if (this.authBusy) return;
        auth.clearChallenge();
        this.state.authStep = 'phone';
        this.state.otp = '';
        this.authBusy = null;
        this.render(true);
        return;

      case 'cancel-auth':
        // Leaving the auth UI does not mint a session from a pending Google cookie.
        auth.clearChallenge();
        this.forceAuthView = false;
        this.authBusy = null;
        this.state.authStep = 'choose';
        this.state.view = 'form';
        this.render(true);
        return;

      case 'retry':
        this.state.view = 'form';
        this.render(true);
        this.collectPhotoFiles();
        void this.flow.submit().then((outcome) => {
          if (outcome.needsAuth) this.enterAuth();
          else if (outcome.result) {
            void this.uploadPhotosFor(outcome.result);
            void this.uploadAttachmentsFor(outcome.result);
          } else if (outcome.error?.code === 'VALIDATION_ERROR') this.revealFirstInvalidField(this.requestForm());
        });
        return;

      case 'new-request':
        this.reset();
        return;

      case 'refresh-requests':
        void this.loadMyRequests();
        return;

      case 'request-help': {
        const reference = (source?.getAttribute('data-request') ?? '').trim();
        const stage = this.myRequests.items.find((item) => item.dto.publicId === reference)?.stage ?? null;
        this.dispatchEvent(
          new CustomEvent(ELEMENT_EVENTS.actionRequired, {
            bubbles: true,
            composed: true,
            detail: {
              service: this.flow.currentService,
              requestPublicId: reference,
              stage,
            },
          }),
        );
        this.announce('برای راهنمایی پرداخت، با پشتیبانی تمومه تماس بگیرید.');
        return;
      }

      case 'pay': {
        const reference = (source?.getAttribute('data-request') ?? '').trim();
        if (reference) await this.startPayment(reference);
        return;
      }

      case 'submit-receipt': {
        const form = source?.closest('form') ?? null;
        if (form) await this.submitReceipt(form);
        return;
      }

      default:
        return;
    }
  }

  /**
   * Start, or resume, payment and hand the browser to the gateway.
   *
   * The button is put into a busy state first so a double tap cannot fire twice,
   * and any failure is shown inline on the same card instead of navigating away
   * from it. The URL itself always comes from the server. When the server says
   * the gateway is under maintenance, the card-transfer dialog opens instead and
   * nothing navigates.
   */
  private async startPayment(reference: string): Promise<void> {
    if (!this.client || !this.flow) return;
    // One payment start at a time, and none while the dialog is open: a second
    // tap during the busy state must not fire a second request.
    if (this.payingRequest || this.cardTransfer) return;
    // Paying a (possibly different) request supersedes any stale gateway
    // message, which would otherwise read as a result for this new attempt.
    this.hideReturnNotice();
    this.payingRequest = reference;
    this.paymentError = null;
    this.renderMyRequests();
    if (this.state.view === 'success') this.render(true);
    this.announce('در حال آماده‌سازی پرداخت…');
    const startedAt = Date.now();

    try {
      const result = await this.client.payments.pay(reference);
      if (result.mode === 'card_transfer') {
        const card = result.cardTransfer;
        if (!card?.cardNumber) {
          throw new CustomerApiError(
            'PAYMENT_PROVIDER_UNAVAILABLE',
            'اطلاعات کارت مقصد در دسترس نیست. لطفاً با پشتیبانی تمومه تماس بگیرید.',
          );
        }
        const remaining = CARD_TRANSFER_MIN_LOADING_MS - (Date.now() - startedAt);
        if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
        this.payingRequest = null;
        this.renderMyRequests();
        if (this.state.view === 'success') this.render(true);
        this.openCardTransfer(reference, card.cardNumber, card.cardHolder);
        this.dispatchEvent(
          new CustomEvent(ELEMENT_EVENTS.paymentCardTransfer, {
            bubbles: true,
            composed: true,
            detail: { service: this.flow.currentService, requestPublicId: reference },
          }),
        );
        return;
      }
      const redirectUrl = result.redirectUrl?.trim();
      if (!redirectUrl) {
        throw new CustomerApiError('PAYMENT_PROVIDER_UNAVAILABLE', 'درگاه پرداخت در دسترس نیست.');
      }
      this.dispatchEvent(
        new CustomEvent(ELEMENT_EVENTS.paymentRedirect, {
          bubbles: true,
          composed: true,
          detail: {
            service: this.flow.currentService,
            requestPublicId: result.request?.publicId ?? reference,
            paymentPublicId: result.payment?.publicId ?? null,
            redirectUrl,
          },
        }),
      );
      this.payingRequest = null;
      navigateTo(redirectUrl);
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      this.payingRequest = null;
      this.paymentError = { reference, message: normalized.messageFa };
      this.announce(normalized.messageFa);
      this.renderMyRequests();
      if (this.state.view === 'success') this.render(true);
    }
  }

  // ---- card-transfer dialog (gateway maintenance) ---------------------------

  /**
   * Open the card-transfer dialog for one request.
   *
   * Showing it changes nothing on the server: the request stays awaiting
   * payment until a receipt is submitted and approved through the existing
   * manual-payment flow.
   */
  private openCardTransfer(reference: string, cardNumber: string, cardHolder: string | null): void {
    if (!this.modalHost) return;
    if (this.myRequestsSheetOpen) this.closeMyRequestsSheet();
    this.cardTransfer = { reference, cardNumber, cardHolder, copy: 'idle' };
    this.modalHost.innerHTML = this.cardTransferHtml();
    this.lockScroll();
    this.announce(CARD_TRANSFER_MESSAGE_FA);
    this.modalHost.querySelector<HTMLElement>('[data-modal-copy]')?.focus();
  }

  /**
   * Open «درخواست‌های من» as a dedicated bottom sheet (account menu entry).
   *
   * Reuses the same list markup and actions as the in-page region so the sheet
   * never forks the requests-list implementation.
   */
  openMyRequestsSheet(): void {
    if (!this.modalHost) return;
    if (this.cardTransfer) this.closeCardTransfer(false);
    this.myRequestsSheetOpen = true;
    void this.loadMyRequests().then(() => this.paintMyRequestsSheet());
    this.paintMyRequestsSheet();
    // The sheet scrolls internally and the page behind it is locked, so opening
    // it must not move the document at all — and it always starts at its own
    // top rather than wherever a previous visit left it.
    const body = this.modalHost.querySelector<HTMLElement>('[data-region="my-requests-sheet"]');
    if (body) body.scrollTop = 0;
    this.lockScroll();
    this.modalHost.querySelector<HTMLElement>('[data-modal-close]')?.focus();
  }

  private paintMyRequestsSheet(): void {
    if (!this.modalHost || !this.myRequestsSheetOpen) return;
    const body = this.modalHost.querySelector<HTMLElement>('[data-region="my-requests-sheet"]');
    if (body) {
      // Patch only the list. Replacing the whole dialog on every refresh would
      // reset the sheet's own scroll position and drop keyboard focus — the
      // "unexpected jump" a customer would feel while a payment state updates.
      body.innerHTML = this.myRequestsHtml({ scope: 'all', heading: false });
      return;
    }
    const titleId = 'mine-sheet-title';
    this.modalHost.innerHTML = `
      <div class="modal-backdrop" data-mine-sheet>
        <div class="modal modal-sheet-tall" role="dialog" aria-modal="true" tabindex="-1"
             aria-labelledby="${titleId}" part="my-requests-dialog">
          <div class="modal-head">
            <div class="modal-head-text">
              <h3 id="${titleId}">درخواست‌های من</h3>
              <p class="modal-sub">${esc(this.allSectionsNote())}</p>
            </div>
            <button class="modal-close" type="button" data-modal-close
                    aria-label="بستن درخواست‌های من">${iconSvg('close', 22)}</button>
          </div>
          <div class="mine-sheet-body" data-region="my-requests-sheet">
            ${this.myRequestsHtml({ scope: 'all', heading: false })}
          </div>
        </div>
      </div>`;
  }

  /**
   * One line naming the sections the sheet covers.
   *
   * Built from the deployment's own enabled catalog rather than a hard-coded
   * sentence, so a site that exposes two sections does not claim three.
   */
  private allSectionsNote(): string {
    const names = (this.client?.services.all() ?? []).map((definition) => definition.nameFa);
    if (names.length === 0) return 'همهٔ درخواست‌های ثبت‌شدهٔ شما.';
    return `همهٔ درخواست‌های شما در بخش‌های ${names.join('، ')}.`;
  }

  private closeMyRequestsSheet(): void {
    if (!this.myRequestsSheetOpen) return;
    this.myRequestsSheetOpen = false;
    if (this.modalHost) this.modalHost.innerHTML = '';
    this.unlockScroll();
  }

  private cardTransferHtml(): string {
    const dialog = this.cardTransfer;
    if (!dialog) return '';
    const id = `ct-${esc(dialog.reference)}`;
    const grouped = dialog.cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
    return `
      <div class="modal-backdrop">
        <div class="modal" role="dialog" aria-modal="true" tabindex="-1"
             aria-labelledby="${id}-title" aria-describedby="${id}-message" part="payment-dialog">
          <div class="modal-head">
            <h3 id="${id}-title">${iconSvg('card', 20)} پرداخت کارت به کارت</h3>
            <button class="modal-close" type="button" data-modal-close aria-label="بستن">${iconSvg('close', 20)}</button>
          </div>
          <p class="modal-message" id="${id}-message">${esc(CARD_TRANSFER_MESSAGE_FA)}</p>
          ${this.checkoutPriceHtml('transfer')}
          <div class="card-box">
            <span class="card-label" id="${id}-card-label">شماره کارت</span>
            <span class="card-number" dir="ltr" aria-labelledby="${id}-card-label" data-card-number>${esc(grouped)}</span>
            ${dialog.cardHolder ? `<span class="card-holder">به نام ${esc(dialog.cardHolder)}</span>` : ''}
          </div>
          <div class="modal-actions">
            <button class="btn" type="button" data-modal-copy data-copy-state="${dialog.copy}">${this.copyButtonInner(dialog.copy)}</button>
            <button class="btn secondary" type="button" data-modal-receipt>${iconSvg('upload', 18)} ارسال فیش</button>
            <button class="btn ghost" type="button" data-modal-close>بستن</button>
          </div>
          <p class="modal-feedback" role="status" aria-live="polite" data-modal-feedback></p>
          <p class="modal-note">کد پیگیری درخواست: <span class="code" dir="ltr">${esc(dialog.reference)}</span></p>
        </div>
      </div>`;
  }

  private copyButtonInner(state: CopyState): string {
    switch (state) {
      case 'copying':
        return '<span class="spinner" aria-hidden="true"></span> در حال کپی…';
      case 'copied':
        return `${iconSvg('check', 18)} کپی شد`;
      default:
        return `${iconSvg('copy', 18)} کپی شماره کارت`;
    }
  }

  /**
   * Patch the copy button and its feedback in place.
   *
   * Never a re-render: replacing the button would drop keyboard focus from the
   * control the customer just pressed.
   */
  private setCopyState(state: CopyState): void {
    if (!this.cardTransfer || !this.modalHost) return;
    this.cardTransfer.copy = state;
    const button = this.modalHost.querySelector<HTMLElement>('[data-modal-copy]');
    if (button) {
      button.innerHTML = this.copyButtonInner(state);
      button.setAttribute('data-copy-state', state);
      if (state === 'copying') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
    const feedback = this.modalHost.querySelector<HTMLElement>('[data-modal-feedback]');
    if (feedback) {
      const messages: Record<CopyState, [string, string]> = {
        idle: ['', ''],
        copying: ['در حال کپی شماره کارت…', ''],
        copied: ['شماره کارت کپی شد.', 'ok'],
        failed: ['کپی خودکار انجام نشد؛ شماره کارت را انتخاب و دستی کپی کنید.', 'error'],
      };
      const [message, tone] = messages[state];
      feedback.textContent = message;
      if (tone) feedback.setAttribute('data-tone', tone);
      else feedback.removeAttribute('data-tone');
    }
  }

  private async copyCardNumber(): Promise<void> {
    const dialog = this.cardTransfer;
    if (!dialog || dialog.copy === 'copying') return;
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
    this.copyResetTimer = null;
    this.setCopyState('copying');
    const copied = await writeClipboard(dialog.cardNumber);
    // The dialog may have been closed while the clipboard was busy.
    if (this.cardTransfer !== dialog) return;
    this.setCopyState(copied ? 'copied' : 'failed');
    this.modalHost?.querySelector<HTMLElement>('[data-modal-copy]')?.focus();
    if (copied) {
      this.copyResetTimer = setTimeout(() => {
        this.copyResetTimer = null;
        if (this.cardTransfer === dialog) this.setCopyState('idle');
      }, COPY_FEEDBACK_MS);
    } else {
      selectNodeText(this.modalHost?.querySelector<HTMLElement>('[data-card-number]') ?? null);
    }
  }

  private closeCardTransfer(restoreFocus: boolean): void {
    const reference = this.cardTransfer?.reference ?? null;
    this.cardTransfer = null;
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
    this.copyResetTimer = null;
    if (this.modalHost) this.modalHost.innerHTML = '';
    this.unlockScroll();
    if (restoreFocus && reference) this.payButtonFor(reference)?.focus();
  }

  /** Escape closes; Tab and Shift+Tab stay inside the dialog. */
  private onDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCardTransfer(true);
      return;
    }
    if (event.key !== 'Tab' || !this.modalHost) return;
    const focusables = Array.from(
      this.modalHost.querySelectorAll<HTMLElement>('button:not([disabled]), [href]'),
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = (this.root?.activeElement as HTMLElement | null) ?? null;
    const inside = active !== null && this.modalHost.contains(active);
    if (event.shiftKey && (!inside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * «ارسال فیش»: take the customer to the existing receipt form.
   *
   * There is exactly one receipt flow — the request's own «ارسال رسید پرداخت»
   * form, which posts to the manual-payment endpoint. This opens it, scrolls it
   * into view and focuses the file field; it never builds a second form.
   */
  private async goToReceipt(reference: string): Promise<void> {
    this.closeCardTransfer(false);
    let form = this.receiptFormFor(reference);
    if (!form) {
      await this.loadMyRequests();
      form = this.receiptFormFor(reference);
    }
    if (!form && this.receiptExpectedFromSheet(reference)) {
      // The request belongs to a different section than the page the customer is
      // on — the sheet lists every section, while this page renders only its own.
      // Opening the sheet renders that request's own receipt form, so the one
      // receipt flow stays reachable from any service. Only done when the sheet
      // can actually hold the form; otherwise the request gets the same explicit
      // explanation it always had.
      this.openMyRequestsSheet();
      await this.loadMyRequests();
      form = this.receiptFormFor(reference);
    }
    if (!form) {
      const item = this.myRequests.items.find((entry) => entry.dto.publicId === reference);
      const message =
        item?.dto.receiptBlockedReason === 'PENDING_REVIEW'
          ? 'رسید پرداخت شما ثبت شده و در انتظار بررسی کارشناس است.'
          : 'ارسال فیش برای این درخواست در حال حاضر امکان‌پذیر نیست. لطفاً با پشتیبانی تمومه تماس بگیرید.';
      this.paymentError = { reference, message };
      this.announce(message);
      this.renderMyRequests();
      if (this.state.view === 'success') this.render(true);
      this.payButtonFor(reference)?.focus();
      return;
    }
    for (let node = form.parentElement; node; node = node.parentElement) {
      if (node.tagName === 'DETAILS') (node as HTMLDetailsElement).open = true;
      if (node === this.stage || node === this.modalHost) break;
    }
    const fileInput = form.querySelector<HTMLElement>('input[name="receipt"]');
    if (fileInput) focusWithoutScroll(fileInput);
    // Reveal it in whichever surface holds it: the page's pinned-chrome band for
    // the in-page region, or the open sheet's own scroll container — scrolling
    // the document behind the sheet would be an unexplained page jump.
    if (this.myRequestsSheetOpen) {
      if (typeof form.scrollIntoView === 'function') form.scrollIntoView({ block: 'center' });
    } else {
      revealBelowPinnedChrome(form, { anchor: 0.5, host: this });
    }
    this.announce('فرم ارسال فیش باز شد. تصویر فیش و توضیح آن را وارد کنید.');
  }

  /**
   * True when the requests sheet is the only surface that can render this
   * request's receipt form.
   *
   * That is exactly the cross-service case: the request is loaded and the server
   * says a receipt is acceptable, but its section is not the one this page
   * renders. Anything else — a request the server refuses a receipt for, or one
   * the customer does not own — must keep the plain explanation instead of
   * opening a sheet that could not help.
   */
  private receiptExpectedFromSheet(reference: string): boolean {
    const entry = this.myRequests.items.find((item) => item.dto.publicId === reference);
    if (!entry) return false;
    if (entry.service === this.definition?.slug) return false;
    return entry.dto.canSubmitReceipt === true;
  }

  /**
   * The receipt form for one request, matched by attribute rather than a CSS
   * selector. Searched across the whole widget — the in-page region and the
   * requests sheet — because the sheet renders its own (identical) card, and the
   * customer's receipt flow must not depend on which of the two they came from.
   */
  private receiptFormFor(reference: string): HTMLFormElement | null {
    const forms = this.root?.querySelectorAll<HTMLFormElement>('form[data-action="submit-receipt"]') ?? [];
    return Array.from(forms).find((form) => form.getAttribute('data-request') === reference) ?? null;
  }

  private payButtonFor(reference: string): HTMLElement | null {
    const buttons = this.root?.querySelectorAll<HTMLElement>('button[data-action="pay"]') ?? [];
    return Array.from(buttons).find((button) => button.getAttribute('data-request') === reference) ?? null;
  }

  private lockScroll(): void {
    if (typeof document === 'undefined' || !document.body || this.scrollLockPrevious !== null) return;
    this.scrollLockPrevious = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockScroll(): void {
    if (typeof document === 'undefined' || !document.body || this.scrollLockPrevious === null) return;
    document.body.style.overflow = this.scrollLockPrevious;
    this.scrollLockPrevious = null;
  }

  private enterAuth(): void {
    this.state.view = 'auth';
    const googleEnabled = Boolean(this.client?.auth.snapshot.authMethods.google);
    const needsPhone = Boolean(this.client?.auth.snapshot.needsPhoneVerification);
    if (needsPhone) {
      this.state.authStep = 'phone';
    } else if (this.state.authStep === 'otp') {
      // Stay on OTP while a challenge is in flight.
    } else if (googleEnabled) {
      this.state.authStep = 'choose';
    } else {
      this.state.authStep = 'phone';
    }
    this.authBusy = null;
    this.render(true);
  }

  /**
   * After a successful sign-in, file the draft the customer already completed.
   *
   * `force` is used when the customer signed in through the in-page OTP form —
   * they clearly meant to continue, so the interrupted submission is retried.
   */
  private async maybeAutoResume(force = false): Promise<void> {
    if (!this.flow || !this.client) return;
    if (!this.client.auth.isAuthenticated) return;
    const snapshot = this.flow.snapshot;
    const shouldResume = force || snapshot.needsAuth;
    if (!shouldResume) {
      if (this.state.view === 'auth') this.render(true);
      return;
    }
    if (this.state.autoResumed && !force) return;
    this.state.autoResumed = true;
    const outcome = await this.flow.resumeAfterAuthentication();
    if (outcome?.result) {
      // The draft was filed after an interrupted sign-in; the photos and
      // attachments the customer picked before that interruption are still in
      // memory.
      this.showReturnNotice({ tone: 'success', message: REQUEST_SUCCESS_TOAST_FA });
      void this.uploadPhotosFor(outcome.result);
      void this.uploadAttachmentsFor(outcome.result);
      this.dispatchEvent(
        new CustomEvent(ELEMENT_EVENTS.requestSuccess, {
          bubbles: true,
          composed: true,
          detail: { service: this.flow.currentService, request: outcome.result.request, resumed: true },
        }),
      );
    }
  }

  private reportAuthError(error: unknown): void {
    const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
    this.announce(normalized.messageFa);
    this.state.view = 'auth';
    this.render(true);
    // The message sits directly under the phone or code field, where the
    // customer is looking, rather than below the buttons.
    const form = this.root?.querySelector('form');
    if (!form) return;
    const input = form.querySelector<HTMLInputElement>('input[name="phone"], input[name="otp"]');
    const field = input?.closest<HTMLElement>('.field') ?? null;
    let node = form.querySelector<HTMLElement>('.error[data-auth-error]');
    if (!node) {
      node = document.createElement('p');
      node.className = 'error';
      node.id = `auth-error-${this.definition?.slug ?? 'service'}`;
      node.setAttribute('data-auth-error', '1');
      node.setAttribute('role', 'alert');
    }
    node.textContent = normalized.messageFa;
    (field ?? form).append(node);
    if (!input) return;
    // Only a problem with what was typed marks the input itself; "the SMS
    // could not be sent" or a cooldown is not the customer's typing.
    if (normalized.code === 'VALIDATION_ERROR' || normalized.code === 'OTP_INVALID') {
      input.setAttribute('aria-invalid', 'true');
      const describedBy = new Set((input.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean));
      describedBy.add(node.id);
      input.setAttribute('aria-describedby', [...describedBy].join(' '));
    }
    focusWithoutScroll(input);
    revealBelowPinnedChrome(field ?? input, { anchor: AUTH_INPUT_ANCHOR, onlyIfObscured: true, host: this });
  }

  /**
   * Take the customer to the first field of `form` that needs correcting.
   *
   * "First" is document order — the top of the form first — not the order the
   * validator reported. The field's wrapper (label, control and message) is
   * what is brought into view below the host page's pinned chrome, and the
   * control is what takes focus, so a screen reader reads the label and the
   * error through `aria-describedby`. When no control is marked (an issue for
   * a field this page does not render), the first error message is revealed.
   */
  private revealFirstInvalidField(form: HTMLElement | null | undefined): void {
    if (!form) return;
    // Validation keeps the keyboard open: cancel any pending chrome restore so
    // the pinned bars stay hidden while the customer fixes the field.
    this.keyboardChrome?.notifyValidationError();
    this.publishKeyboardState();
    const control = form.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (control) {
      focusWithoutScroll(control);
      revealBelowPinnedChrome(control.closest('.field') ?? control, { anchor: 0.4, onlyIfObscured: true, host: this });
      return;
    }
    const message = form.querySelector<HTMLElement>('.error');
    if (message) revealBelowPinnedChrome(message, { anchor: 0.4, onlyIfObscured: true, host: this });
  }

  private requestForm(): HTMLFormElement | null {
    return this.root?.querySelector<HTMLFormElement>('form[data-action="submit"]') ?? null;
  }
}

/** Current location, or a harmless placeholder outside a browser. */
function currentLocation(): string {
  return typeof location === 'undefined' ? '/' : location.href;
}

/**
 * Core's rejection of the phone number, reworded for the customer.
 *
 * Core answers a malformed phone with the generic validation message (naming
 * `phone` in `details.fields`) or with its own sentence and no fields. Any other
 * failure, including a validation issue on another field, is returned as null
 * and shown as Core worded it.
 */
function phoneRejection(error: unknown): CustomerApiError | null {
  if (!(error instanceof CustomerApiError) || error.code !== 'VALIDATION_ERROR') return null;
  const issues = error.fieldIssues;
  if (issues.length > 0 && !issues.some((issue) => issue.field === 'phone')) return null;
  return new CustomerApiError('VALIDATION_ERROR', INVALID_PHONE_MESSAGE_FA, {
    status: error.status,
    details: error.details,
    fieldIssues: issues,
    ...(error.requestId ? { requestId: error.requestId } : {}),
  });
}

/** The tone and text of a return-from-gateway notice. */
export interface ReturnNotice {
  tone: 'success' | 'error' | 'info';
  message: string;
}

/**
 * The notice a return from the payment gateway should show.
 *
 * Core's callback redirects back with `?payment=paid|already_paid|failed|
 * cancelled`. The caller shows it exactly once and then strips those params
 * (`clearGatewayReturnParams`), so it can never be replayed for a request that
 * is not paid. An unknown value is ignored rather than shown as a raw token.
 */
export function gatewayReturnNotice(
  search: string = typeof location === 'undefined' ? '' : location.search,
): ReturnNotice | null {
  if (!search) return null;
  let outcome: string | null = null;
  try {
    outcome = new URLSearchParams(search).get('payment');
  } catch {
    return null;
  }
  if (!outcome) return null;
  switch (outcome.toLowerCase()) {
    case 'paid':
    case 'already_paid':
      return { tone: 'success', message: 'پرداخت با موفقیت انجام شد.' };
    case 'failed':
      return { tone: 'error', message: 'پرداخت انجام نشد. می‌توانید دوباره تلاش کنید.' };
    case 'cancelled':
      return {
        tone: 'info',
        message: 'پرداخت لغو شد. هر زمان بخواهید می‌توانید دوباره پرداخت کنید.',
      };
    default:
      return null;
  }
}

/**
 * Drop the gateway's return parameters from the address bar.
 *
 * Only the notice params are removed; `request` and `service` stay, because the
 * widget still needs them to render the right section and request. Keeping the
 * URL clean is what makes the toast one-shot without any stored state.
 */
export function clearGatewayReturnParams(): void {
  if (typeof location === 'undefined' || typeof history === 'undefined') return;
  try {
    const url = new URL(location.href);
    let changed = false;
    for (const key of ['payment', 'message', 'paymentId']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) history.replaceState(history.state, '', url.toString());
  } catch {
    // A malformed or unparseable URL simply keeps its query string; the toast is
    // already shown and nothing else depends on the cleanup.
  }
}

/** What Core's Google callback (or the header login link) may put on the URL. */
export interface GoogleAuthReturn {
  needsPhone: boolean;
  /** Header «ورود به حساب» opens the method chooser. */
  openLogin: boolean;
  /** Full Google session issued; show a one-shot success toast. */
  loginSuccess: boolean;
  /** Customer-facing Persian error, already length-capped. */
  errorMessage: string | null;
}

const GOOGLE_AUTH_ERROR_MAX_LEN = 200;

/**
 * Parse `?auth=phone` / `?auth=login` / `?auth=ok` / `?auth_error=…` from a return URL.
 *
 * Unknown `auth` values are ignored. `auth_error` is treated as opaque text
 * (rendered via `textContent` / `esc`), never as HTML. `auth=ok` never pairs
 * with an error toast: an error wins if both are present.
 */
export function googleAuthReturn(
  search: string = typeof location === 'undefined' ? '' : location.search,
): GoogleAuthReturn | null {
  if (!search) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  } catch {
    return null;
  }
  const auth = params.get('auth')?.toLowerCase() ?? '';
  const needsPhone = auth === 'phone';
  const openLogin = auth === 'login';
  const rawError = params.get('auth_error');
  const errorMessage =
    rawError && rawError.trim()
      ? rawError.trim().slice(0, GOOGLE_AUTH_ERROR_MAX_LEN)
      : null;
  const loginSuccess = auth === 'ok' && !errorMessage;
  if (!needsPhone && !openLogin && !loginSuccess && !errorMessage) return null;
  return { needsPhone, openLogin, loginSuccess, errorMessage };
}

/** Drop Google-auth return params after they have been consumed once. */
export function clearGoogleAuthReturnParams(): void {
  if (typeof location === 'undefined' || typeof history === 'undefined') return;
  try {
    const url = new URL(location.href);
    let changed = false;
    for (const key of ['auth', 'auth_error']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) history.replaceState(history.state, '', url.toString());
  } catch {
    // Keep the query string; the notice/view change already happened.
  }
}

/** In-memory `StorageLike` used when `sessionStorage` is unavailable. */
function memoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  };
}

/**
 * Read configuration from the document.
 *
 * Implemented here rather than imported from `config.ts` to keep the element
 * free of a document dependency in Node tests: outside a browser it simply
 * returns null and the element renders its "not configured" state.
 */
function readDocumentConfig(): CustomerClientConfig | null {
  if (typeof document === 'undefined') return null;
  const globalConfig = (globalThis as { TAMOOMEH_CUSTOMER_CONFIG?: CustomerClientConfig })
    .TAMOOMEH_CUSTOMER_CONFIG;
  if (globalConfig?.apiBaseUrl) return globalConfig;
  const meta = document.querySelector('meta[name="tamoomeh:api-base"]')?.getAttribute('content');
  if (!meta) return null;
  const script = document.querySelector('script[data-tamoomeh-customer]');
  const attr = (name: string): string | null => script?.getAttribute(name) ?? null;
  return {
    apiBaseUrl: meta,
    authMode: (meta2('tamoomeh:auth-mode') ?? 'same-site') as CustomerClientConfig['authMode'],
    ...(meta2('tamoomeh:bridge-session')
      ? {
          bridge: {
            sessionUrl: meta2('tamoomeh:bridge-session')!,
            loginUrl: meta2('tamoomeh:bridge-login')!,
            logoutUrl: meta2('tamoomeh:bridge-logout')!,
            requestUrl: meta2('tamoomeh:bridge-request')!,
          },
        }
      : {}),
    ...(attr('data-debug') === 'true' ? { debug: true } : {}),
  };

  function meta2(name: string): string | null {
    return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null;
  }
}

/** Keep the icon catalog and the service catalog from drifting apart. */
export function missingServiceIcons(): string[] {
  return SERVICE_REQUEST_ICON_GUARD.filter((icon) => !hasIcon(icon));
}

const SERVICE_REQUEST_ICON_GUARD = ['scale', 'dumbbell', 'child'];

/**
 * Register the element. Idempotent, so a page that loads the bundle twice (or a
 * host that also defines it) does not throw.
 */
export function defineServiceRequestElement(tagName: string = SERVICE_REQUEST_TAG): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get(tagName)) return;
  customElements.define(tagName, ServiceRequestElement);
}
